import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { useNavigate, Link } from "react-router-dom";
import { BadgeCheck, X, BarChart3, Check, Download, Trash2, Pencil, Shield, Plus, Pin, MessagesSquare, Users, Ban, Image as ImageIcon, Trophy, Award, FileText, Flag, GraduationCap, Eye } from "lucide-react";
import SchoolPhotosManager from "@/components/site/SchoolPhotosManager";
import UserLevelBadge from "@/components/site/UserLevelBadge";
import { TYPE_OPTIONS, GRADE_OPTIONS, CURRICULUM_OPTIONS, EDU_LEVEL_OPTIONS, RELIGION_OPTIONS } from "@/lib/taxonomy";
import CheckboxGroup from "@/components/admin/CheckboxGroup";
import CurriculumPicker from "@/components/admin/CurriculumPicker";
import NpsnVerifier from "@/components/admin/NpsnVerifier";
import CampusPicker from "@/components/admin/CampusPicker";
import MoneyInput from "@/components/site/MoneyInput";
import Field from "@/components/admin/Field";
import SchoolsTab from "@/components/admin/SchoolsTab";
import CurriculumMigrationPanel from "@/components/admin/CurriculumMigrationPanel";
import HierarchyTab from "@/components/admin/HierarchyTab";
import ReviewsTab from "@/components/admin/ReviewsTab";
import ForumTab from "@/components/admin/ForumTab";
import RuangAdminPanel from "@/components/admin/RuangAdminPanel";
import UsersTab from "@/components/admin/UsersTab";
import ArticleDiscussionsTab from "@/components/admin/ArticleDiscussionsTab";
import { PROVINCE_NAMES, getCitiesForProvince } from "@/lib/idProvinces";
import PhotoUploader from "@/components/site/PhotoUploader";
import ImageCropUploader from "@/components/site/ImageCropUploader";
import ImageUploader from "@/components/site/ImageUploader";
import BlogEditor from "@/components/admin/BlogEditor";
import GmapsLocationInput from "@/components/admin/GmapsLocationInput";
import ReviewPhotosPicker from "@/components/site/ReviewPhotosPicker";
import AlumniOutcomes from "@/components/site/AlumniOutcomes";
import ForumConsole from "@/components/site/ForumConsole";
import UserDetailDrawer from "@/components/admin/UserDetailDrawer";
import { Switch } from "@/components/ui/switch";
import { setGamificationEnabled } from "@/components/site/UserLevelBadge";
import { promptLogin, promptRegister } from "@/lib/authRedirect";

const PROOF_LABEL = { invoice: "Tuition invoice", receipt: "Payment receipt", statement: "School fee statement" };

// Stable, module-scope reference so React state initializers don't get a fresh
// object on every render (avoids subtle bugs where auto-save or effects reset
// controlled inputs mid-typing).
const EMPTY_PAGE_FORM = Object.freeze({ slug: "", title: "", body: "", published: true });

// Canonical Journal taxonomy (mirror of backend `ALLOWED_JOURNAL_CATEGORIES`).
// Editing this list without updating routes/blog.py will cause 422 on save.
const JOURNAL_CATEGORIES = Object.freeze([
  { slug: "sosok-perspektif", label: "Sosok & Perspektif" },
  { slug: "biaya-keuangan", label: "Biaya & Keuangan" },
  { slug: "belajar-kurikulum", label: "Belajar & Kurikulum" },
  { slug: "kuliah-masa-depan", label: "Kuliah & Masa Depan" },
  { slug: "school-spotlight", label: "School Spotlight" },
  { slug: "data-insight", label: "Data & Insight" },
  { slug: "kehidupan-siswa", label: "Kehidupan Siswa" },
  { slug: "panduan-pertimbangan", label: "Panduan & Pertimbangan" },
]);
const JOURNAL_CATEGORY_LABEL = Object.freeze(
  JOURNAL_CATEGORIES.reduce((m, c) => ({ ...m, [c.slug]: c.label }), {})
);

/**
 * TagsInput — free-form tag editor that lets the user type commas & spaces
 * naturally. Parses to a string[] only on blur / Enter, so a comma keypress
 * never wipes the trailing empty token (which the previous "join(', ')" +
 * "split(',').filter(Boolean)" pattern did — the tag input on the Journal
 * composer had become unusable because commas got swallowed).
 */
const TagsInput = ({ value, onChange }) => {
  const [text, setText] = React.useState((value || []).join(", "));
  // Rehydrate the buffer whenever the parent hands us a different tags array
  // (e.g. loading an existing post for edit).
  React.useEffect(() => {
    setText((value || []).join(", "));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [(value || []).join("|")]);
  const commit = (raw) => {
    const parsed = (raw || "")
      .split(",")
      .map((t) => t.trim())
      .filter(Boolean);
    onChange(parsed);
  };
  return (
    <Input
      placeholder="Tag (pisah dengan koma, mis: kurikulum, panduan)"
      value={text}
      onChange={(e) => setText(e.target.value)}
      onBlur={() => commit(text)}
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          e.preventDefault();
          commit(text);
        }
      }}
      data-testid="post-tags"
    />
  );
};

/**
 * AliasesInput — raw-text buffer for school aliases so the user can type
 * spaces, commas, hyphens, parentheses, periods, ampersands, apostrophes
 * naturally. Never sanitises during onChange. Only splits on comma → trims
 * → drops blanks → case-insensitive dedupes at commit time (onBlur / Enter /
 * form save). Rehydrates from `aliases[]` when the parent swaps in a
 * different school (edit dialog opening a fresh row). Backend continues to
 * run `_normalize_aliases()` as the final canonical pass so the UI helper
 * never diverges from server truth.
 */
const AliasesInput = ({ value, onChange, testId, placeholder }) => {
  const [text, setText] = React.useState((value || []).join(", "));
  React.useEffect(() => {
    setText((value || []).join(", "));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [(value || []).join("|")]);
  const commit = (raw) => {
    const seen = new Set();
    const parsed = [];
    for (const piece of (raw || "").split(",")) {
      const t = piece.trim();
      if (!t) continue;
      const key = t.toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      parsed.push(t);
    }
    onChange(parsed);
  };
  return (
    <Input
      placeholder={placeholder || "mis: BSJ, SIS, Al Azhar Kebayoran"}
      value={text}
      onChange={(e) => setText(e.target.value)}
      onBlur={() => commit(text)}
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          e.preventDefault();
          commit(text);
        }
      }}
      data-testid={testId}
    />
  );
};

export default function Admin() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [pending, setPending] = useState([]);
  const [verifs, setVerifs] = useState([]);
  const [stats, setStats] = useState(null);
  const [schools, setSchools] = useState([]);
  // Actual DB-side total (independent of pagination/limit). Rendered as the
  // Kelola Sekolah counter so admins see the truth even if the fetched list
  // is bigger/smaller than what's currently visible in-page.
  const [schoolsTotal, setSchoolsTotal] = useState(0);
  const [reviews, setReviews] = useState([]);
  const [admins, setAdmins] = useState([]);
  const [previewImg, setPreviewImg] = useState(null);
  const [editingSchool, setEditingSchool] = useState(null);
  const [newSchoolOpen, setNewSchoolOpen] = useState(false);
  const [hierarchyRefreshKey, setHierarchyRefreshKey] = useState(0);
  const emptyNewSchool = {
    name: "", city: "", state: "", school_type: "swasta",
    address_street: "", kelurahan: "", kecamatan: "",
    curriculum: [], curriculum_detail: "", description: "",
    website: "", latitude: "", longitude: "",
    brand_id: "", campus_id: null, branch_name: "", education_levels: [], grade_levels: [],
    entry_fee_min: "", entry_fee_max: "",
    spp_min: "", spp_max: "", other_annual_fees: "", fee_academic_year: "",
    accreditation_grade: "", accreditation_year: "", teacher_to_student_ratio: "", alumni_achievements: "",
    npsn: "", students_count: "", teachers_count: "",
    religion_affiliation: "",
    image_url: "",
    aliases: [],
  };
  const [newSchoolForm, setNewSchoolForm] = useState(emptyNewSchool);
  const [newSchoolPhotos, setNewSchoolPhotos] = useState([]);

  // Kelola Sekolah — search & filter
  const [schoolQuery, setSchoolQuery] = useState("");
  const [schoolProvinceFilter, setSchoolProvinceFilter] = useState("all");
  const [schoolLevelFilter, setSchoolLevelFilter] = useState("all");
  const [schoolTypeFilter, setSchoolTypeFilter] = useState("all");
  const filteredSchools = React.useMemo(() => {
    const q = schoolQuery.trim().toLowerCase();
    return schools.filter((s) => {
      if (q) {
        const hay = `${s.name || ""} ${s.city || ""} ${s.state || ""} ${(s.npsn || "")}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      if (schoolProvinceFilter !== "all" && (s.state || "") !== schoolProvinceFilter) return false;
      if (schoolLevelFilter !== "all" && !(s.education_levels || []).includes(schoolLevelFilter)) return false;
      if (schoolTypeFilter !== "all" && (s.school_type || "") !== schoolTypeFilter) return false;
      return true;
    });
  }, [schools, schoolQuery, schoolProvinceFilter, schoolLevelFilter, schoolTypeFilter]);

  // CSV bulk import
  const [importOpen, setImportOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const [importBusy, setImportBusy] = useState(false);

  // Static pages CMS
  const [staticPages, setStaticPages] = useState([]);
  const [editingPage2, setEditingPage2] = useState(null); // null | 'new' | {slug,...}
  const [pageForm2, setPageForm2] = useState(EMPTY_PAGE_FORM);
  const [newAdminOpen, setNewAdminOpen] = useState(false);
  const [newAdmin, setNewAdmin] = useState({ email: "", password: "", name: "", role: "admin" });

  const isSuper = user?.role === "superadmin";
  const isAdminOrSuper = user?.role === "admin" || user?.role === "superadmin";
  const isModerator = user?.role === "moderator";
  const isModOrAbove = isAdminOrSuper || isModerator;
  const [blogPosts, setBlogPosts] = useState([]);
  const [editingPost, setEditingPost] = useState(null);
  const [newPostOpen, setNewPostOpen] = useState(false);
  const emptyPost = {
    title: "", excerpt: "", body: "", cover_url: "", tags: [],
    categories: [], school_ids: [],
    content_type: "insight", is_featured: false, key_stats: [],
    pdf_url: "", pdf_size_bytes: null, author_title: "", author_avatar_url: "",
    read_time_min: null, takeaway: "", slug: "", status: "draft",
  };
  const [postForm, setPostForm] = useState(emptyPost);
  // Admin blog-list filter (client-side; single-select from 8 canonical slugs).
  // Empty string = show all. Multi-category articles match when any slug fits.
  const [blogCategoryFilter, setBlogCategoryFilter] = useState("");
  // Local buffer for the "Sekolah Terkait" searchable multi-select. Reset when
  // the composer opens/closes; drives client-side filtering over `schools`.
  const [schoolPickerQuery, setSchoolPickerQuery] = useState("");

  // Community management state
  const [forumCats, setForumCats] = useState([]);
  const [allThreads, setAllThreads] = useState([]);
  const [allReplies, setAllReplies] = useState([]);
  // Default sub-tab of "Ruang & Diskusi": admin+ lands on Ruang (Kelola Ruang);
  // moderators (who can't manage Ruang CRUD) land on Diskusi listing.
  const [communityTab, setCommunityTab] = useState("threads");
  const [activeTab, setActiveTab] = useState(null); // null = use default; set programmatically for handoff
  const [pendingThreadsFilterRuang, setPendingThreadsFilterRuang] = useState("");
  const [editingCat, setEditingCat] = useState(null);
  const [newCatOpen, setNewCatOpen] = useState(false);
  const emptyCat = { slug: "", label: "", description: "", order: 100 };
  const [catForm, setCatForm] = useState(emptyCat);

  // Users management state (super admin only)
  const [allUsers, setAllUsers] = useState([]);
  const [usersSortBy, setUsersSortBy] = useState("last_active");
  const [detailUserId, setDetailUserId] = useState(null); // opens UserDetailDrawer when set

  const sortedAllUsers = React.useMemo(() => {
    const arr = [...allUsers];
    if (usersSortBy === "joined") {
      arr.sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
    } else if (usersSortBy === "joined_asc") {
      arr.sort((a, b) => (a.created_at || "").localeCompare(b.created_at || ""));
    } else if (usersSortBy === "contribution") {
      const score = (u) => (u.review_count || 0) + (u.thread_count || 0) + (u.reply_count || 0);
      arr.sort((a, b) => score(b) - score(a));
    } else {
      // last_active default
      arr.sort((a, b) => (b.last_active || b.created_at || "").localeCompare(a.last_active || a.created_at || ""));
    }
    return arr;
  }, [allUsers, usersSortBy]);
  const [usersQuery, setUsersQuery] = useState("");
  const [usersRoleFilter, setUsersRoleFilter] = useState("all");
  const [usersBannedFilter, setUsersBannedFilter] = useState("all");
  const [banDialogUser, setBanDialogUser] = useState(null);
  const [banForm, setBanForm] = useState({ reason: "", duration_days: 0 });

  // Admin action log (super admin only)
  const [adminActions, setAdminActions] = useState([]);
  const [actionFilter, setActionFilter] = useState("all");

  // Photo moderation state (moderator+)
  const [allPhotos, setAllPhotos] = useState([]);
  const [photoSourceFilter, setPhotoSourceFilter] = useState("all");
  const [photoPinFilter, setPhotoPinFilter] = useState("all");
  const [photoReports, setPhotoReports] = useState([]);
  const [photoReportStatus, setPhotoReportStatus] = useState("open");

  // Gamification (level config + leaderboard)
  const [leaderboard, setLeaderboard] = useState([]);
  const [, setLevelConfig] = useState({ levels: [], points: { review: 10, photo: 2, thread: 3, reply: 1 } });
  const [levelConfigDraft, setLevelConfigDraft] = useState(null);

  // Moderator applications
  const [modApps, setModApps] = useState([]);
  const [modAppsStatus, setModAppsStatus] = useState("pending");
  const [reviewingApp, setReviewingApp] = useState(null);
  const [reviewNote, setReviewNote] = useState("");

  // Blog draft auto-save
  const [draftSavedAt, setDraftSavedAt] = useState(null);
  const [draftSaving, setDraftSaving] = useState(false);

  // Review moderation (post-mod)
  const [reviewVerFilter, setReviewVerFilter] = useState("all");
  const [reviewReports, setReviewReports] = useState([]);
  const [reviewReportStatus, setReviewReportStatus] = useState("open");
  // Forum reports (moderation queue for thread + reply reports from users)
  const [forumReports, setForumReports] = useState([]);
  const [forumReportStatus, setForumReportStatus] = useState("open");
  const [reviewRoleFilter, setReviewRoleFilter] = useState("all"); // all|parent|student|alumni
  const [reviewEliteOnly, setReviewEliteOnly] = useState(false);
  const [reviewGradMin, setReviewGradMin] = useState("");
  const [reviewGradMax, setReviewGradMax] = useState("");
  // Notification counts — Review masuk, Pengguna, Komunitas (auto-refreshed every 60s).
  const [notifCounts, setNotifCounts] = useState({ pending_reviews: 0, new_users_24h: 0, new_community_since_login: 0 });

  const loadReviews = (verFilter = reviewVerFilter, roleF = reviewRoleFilter, eliteF = reviewEliteOnly, gMin = reviewGradMin, gMax = reviewGradMax) => {
    const params = { limit: 100 };
    if (verFilter && verFilter !== "all") params.verified = verFilter;
    if (roleF && roleF !== "all") params.reviewer_role = roleF;
    if (eliteF) params.elite_only = "true";
    if (gMin) params.grad_year_min = parseInt(gMin, 10);
    if (gMax) params.grad_year_max = parseInt(gMax, 10);
    return api.get("/admin/reviews", { params }).then((r) => setReviews(r.data)).catch(() => {});
  };

  const verifyReview = async (rid, verified) => {
    try {
      await api.patch(`/admin/reviews/${rid}/verify`, { verified });
      toast.success(verified ? "Review diverifikasi tim" : "Verifikasi dibatalkan");
      loadReviews();
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal"); }
  };

  const loadReviewReports = () => {
    const params = { limit: 200, status: reviewReportStatus };
    return api.get("/admin/review-reports", { params }).then((r) => setReviewReports(r.data)).catch(() => {});
  };

  const resolveReviewReport = async (rep, status) => {
    try {
      await api.patch(`/admin/review-reports/${rep.report_id}`, { status });
      toast.success(status === "actioned" ? "Ditandai ditindaklanjuti" : "Laporan ditutup");
      loadReviewReports();
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal"); }
  };

  const deleteReportedReview = async (rep) => {
    if (!window.confirm("Hapus review yang dilaporkan? Aksi ini permanen.")) return;
    try {
      await api.delete(`/admin/reviews/${rep.review_id}`);
      await api.patch(`/admin/review-reports/${rep.report_id}`, { status: "actioned" });
      toast.success("Review dihapus & laporan ditutup");
      loadReviewReports();
      loadReviews();
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal"); }
  };

  // ---- Forum reports (thread/reply moderation) ----
  const loadForumReports = () => {
    const params = { limit: 200, status: forumReportStatus };
    return api.get("/admin/forum-reports", { params }).then((r) => setForumReports(r.data)).catch(() => {});
  };

  const resolveForumReport = async (rep, status) => {
    try {
      await api.patch(`/admin/forum-reports/${rep.report_id}`, { status });
      toast.success(status === "actioned" ? "Ditandai ditindaklanjuti" : "Laporan ditutup");
      loadForumReports();
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal"); }
  };

  const moderateForumTarget = async (rep, mode) => {
    const msg = mode === "delete"
      ? "Hapus permanen konten ini? Tidak bisa dipulihkan. Postingan tetap terlihat di alur dengan pesan moderasi."
      : "Sembunyikan konten ini? Postingan tetap tampil di alur dengan pesan moderasi dan bisa dipulihkan.";
    if (!window.confirm(msg)) return;
    try {
      const endpoint = rep.target_type === "thread"
        ? `/admin/forum/threads/${rep.target_id}/moderate`
        : `/admin/forum/replies/${rep.target_id}/moderate`;
      await api.post(endpoint, { mode, reason: rep.reason || "" });
      toast.success(mode === "delete" ? "Konten dihapus permanen" : "Konten disembunyikan");
      loadForumReports();
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal"); }
  };

  const unhideForumTarget = async (rep) => {
    if (!window.confirm("Pulihkan konten ini?")) return;
    try {
      const endpoint = rep.target_type === "thread"
        ? `/admin/forum/threads/${rep.target_id}/unhide`
        : `/admin/forum/replies/${rep.target_id}/unhide`;
      await api.post(endpoint);
      toast.success("Konten dipulihkan");
      loadForumReports();
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal"); }
  };

  const load = async () => {
    const calls = [
      api.get("/admin/reviews?limit=100").then((r) => setReviews(r.data)).catch(() => {}),
      api.get("/admin/notification-counts").then((r) => setNotifCounts(r.data)).catch(() => {}),
      api.get("/forum/categories").then((r) => setForumCats(r.data)).catch(() => {}),
      api.get("/admin/threads?limit=200").then((r) => setAllThreads(r.data)).catch(() => {}),
      api.get("/admin/replies?limit=200").then((r) => setAllReplies(r.data)).catch(() => {}),
      loadAllPhotos(),
      loadPhotoReports(),
      loadReviewReports(),
      loadForumReports(),
      loadModApps(),
    ];
    if (isAdminOrSuper) {
      calls.push(
        api.get("/admin/schools/suggestions").then((r) => setPending(r.data)).catch(() => {}),
        api.get("/admin/verifications").then((r) => setVerifs(r.data)).catch(() => {}),
        api.get("/admin/verifications/stats").then((r) => setStats(r.data)).catch(() => {}),
        api.get("/admin/schools/all").then((r) => {
          // New endpoint returns `{items, total, limit}`; older cached JS bundles
          // still using the array shape will get `r.data` as an array.
          const payload = r.data;
          if (Array.isArray(payload)) {
            setSchools(payload);
            setSchoolsTotal(payload.length);
          } else {
            setSchools(payload.items || []);
            setSchoolsTotal(Number(payload.total) || 0);
          }
        }).catch(() => {}),
        api.get("/blog", { params: { include_drafts: true } }).then((r) => setBlogPosts(r.data)).catch(() => {}),
        api.get("/admin/pages").then((r) => setStaticPages(r.data)).catch(() => {}),
      );
    }
    if (isSuper) calls.push(
      api.get("/admin/users").then((r) => setAdmins(r.data)).catch(() => {}),
      loadAllUsers(),
      loadAdminActions(),
      loadLeaderboard(),
      loadLevelConfig(),
    );
    await Promise.all(calls);
  };

  const loadAllUsers = () => {
    const params = {};
    if (usersQuery) params.q = usersQuery;
    if (usersRoleFilter && usersRoleFilter !== "all") params.role = usersRoleFilter;
    if (usersBannedFilter === "banned") params.banned = "true";
    if (usersBannedFilter === "active") params.banned = "false";
    return api.get("/admin/users/all", { params }).then((r) => setAllUsers(r.data)).catch(() => {});
  };

  const loadAdminActions = () => {
    const params = { limit: 200 };
    if (actionFilter && actionFilter !== "all") params.action = actionFilter;
    return api.get("/admin/actions", { params }).then((r) => setAdminActions(r.data)).catch(() => {});
  };

  const loadAllPhotos = () => {
    const params = { limit: 300 };
    if (photoSourceFilter && photoSourceFilter !== "all") params.source = photoSourceFilter;
    if (photoPinFilter === "pinned") params.pinned = "true";
    if (photoPinFilter === "unpinned") params.pinned = "false";
    return api.get("/admin/photos", { params }).then((r) => setAllPhotos(r.data)).catch(() => {});
  };

  const togglePhotoPin = async (p) => {
    try {
      await api.patch(`/admin/photos/${p.photo_id}/pin`, { pinned: !p.pinned });
      toast.success(p.pinned ? "Pin dilepas" : "Foto dipinkan di galeri sekolah");
      loadAllPhotos();
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal"); }
  };
  const deletePhoto = async (p) => {
    if (!window.confirm("Hapus foto ini permanen?")) return;
    try {
      await api.delete(`/admin/photos/${p.photo_id}`);
      toast.success("Foto dihapus");
      loadAllPhotos();
      loadPhotoReports();
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal"); }
  };

  const loadPhotoReports = () => {
    const params = { limit: 200, status: photoReportStatus };
    return api.get("/admin/photo-reports", { params }).then((r) => setPhotoReports(r.data)).catch(() => {});
  };

  const resolvePhotoReport = async (r, status) => {
    try {
      await api.patch(`/admin/photo-reports/${r.report_id}`, { status });
      toast.success(status === "actioned" ? "Ditandai ditindaklanjuti" : "Laporan ditutup");
      loadPhotoReports();
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal"); }
  };

  const deleteReportedPhoto = async (r) => {
    if (!window.confirm("Hapus foto yang dilaporkan?")) return;
    try {
      await api.delete(`/admin/photos/${r.photo_id}`);
      await api.patch(`/admin/photo-reports/${r.report_id}`, { status: "actioned" });
      toast.success("Foto dihapus & laporan ditutup");
      loadPhotoReports();
      loadAllPhotos();
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal"); }
  };

  // ===== Gamification handlers =====
  const loadLeaderboard = () => api.get("/admin/leaderboard?limit=50").then((r) => setLeaderboard(r.data)).catch(() => {});
  const loadLevelConfig = () => api.get("/level-config").then((r) => { setLevelConfig(r.data); setLevelConfigDraft(JSON.parse(JSON.stringify(r.data))); }).catch(() => {});

  const saveLevelConfig = async () => {
    if (!levelConfigDraft) return;
    // Sort levels by min_points to make sure order is consistent
    const cleanLevels = levelConfigDraft.levels.map((l) => ({ name: (l.name || "").trim(), min_points: Number(l.min_points) || 0 })).filter((l) => l.name);
    if (cleanLevels.length < 2) { toast.error("Minimal 2 tingkat level"); return; }
    try {
      const payload = { levels: cleanLevels, points: levelConfigDraft.points };
      if (typeof levelConfigDraft.enabled === "boolean") payload.enabled = levelConfigDraft.enabled;
      const r = await api.patch("/admin/level-config", payload);
      toast.success(`Konfigurasi disimpan · ${r.data.recomputed} pengguna dihitung ulang`);
      // Notify every mounted UserLevelBadge to hide/show immediately (no page reload)
      setGamificationEnabled(payload.enabled !== false);
      loadLevelConfig();
      loadLeaderboard();
      loadAllUsers();
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal menyimpan"); }
  };

  const toggleGamification = async (v) => {
    // Optimistic + persistent — sync draft, persist, and broadcast the change globally.
    setLevelConfigDraft((d) => ({ ...(d || {}), enabled: !!v }));
    try {
      await api.patch("/admin/level-config", {
        levels: (levelConfigDraft?.levels || []).map((l) => ({ name: l.name, min_points: Number(l.min_points) || 0 })),
        points: levelConfigDraft?.points || {},
        enabled: !!v,
      });
      setGamificationEnabled(!!v);
      toast.success(v ? "Gamifikasi diaktifkan" : "Gamifikasi dinonaktifkan — kontribusi tetap direkam");
    } catch (e) {
      setLevelConfigDraft((d) => ({ ...(d || {}), enabled: !v }));
      toast.error(e.response?.data?.detail || "Gagal mengubah status gamifikasi");
    }
  };

  const updateDraftLevel = (idx, key, val) => {
    setLevelConfigDraft((d) => {
      const nd = { ...d, levels: [...d.levels] };
      nd.levels[idx] = { ...nd.levels[idx], [key]: val };
      return nd;
    });
  };
  const updateDraftPoints = (key, val) => setLevelConfigDraft((d) => ({ ...d, points: { ...d.points, [key]: Number(val) || 0 } }));

  // ===== New school (admin curated) =====
  const createSchool = async () => {
    if (!newSchoolForm.name || !newSchoolForm.city) { toast.error("Nama & Kota wajib diisi"); return; }
    if (!newSchoolForm.education_levels.length) { toast.error("Pilih minimal satu jenjang pendidikan"); return; }
    try {
      const payload = {
        ...newSchoolForm,
        entry_fee_min: newSchoolForm.entry_fee_min ? Number(newSchoolForm.entry_fee_min) : null,
        entry_fee_max: newSchoolForm.entry_fee_max ? Number(newSchoolForm.entry_fee_max) : null,
        spp_min: newSchoolForm.spp_min ? Number(newSchoolForm.spp_min) : null,
        spp_max: newSchoolForm.spp_max ? Number(newSchoolForm.spp_max) : null,
        other_annual_fees: newSchoolForm.other_annual_fees ? Number(newSchoolForm.other_annual_fees) : null,
        latitude: newSchoolForm.latitude ? Number(newSchoolForm.latitude) : null,
        longitude: newSchoolForm.longitude ? Number(newSchoolForm.longitude) : null,
        accreditation_year: newSchoolForm.accreditation_year ? Number(newSchoolForm.accreditation_year) : null,
        students_count: newSchoolForm.students_count ? Number(newSchoolForm.students_count) : null,
        teachers_count: newSchoolForm.teachers_count ? Number(newSchoolForm.teachers_count) : null,
        npsn: (newSchoolForm.npsn || "").trim() || null,
        image_url: newSchoolForm.image_url || newSchoolPhotos[0] || "",
        gallery_images: newSchoolPhotos,
      };
      const r = await api.post("/schools", payload);
      // Upload additional photos to the new photos collection (source=admin_direct)
      const schoolId = r.data.school_id;
      for (const dataUrl of newSchoolPhotos) {
        // eslint-disable-next-line no-await-in-loop
        await api.post(`/schools/${schoolId}/photos`, { data_url: dataUrl, caption: "" }).catch(() => {});
      }
      // If admin entered NPSN, immediately verify against Kemdikbud to persist the badge.
      if (payload.npsn) {
        await api.post("/admin/schools/verify-npsn", { npsn: payload.npsn, school_id: schoolId }).catch(() => {});
      }
      toast.success("Sekolah berhasil ditambahkan");
      setNewSchoolOpen(false);
      setNewSchoolForm(emptyNewSchool);
      setNewSchoolPhotos([]);
      setHierarchyRefreshKey((k) => k + 1);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal menambah sekolah"); }
  };

  const toggleNewSchoolEduLevel = (g) => setNewSchoolForm((f) => {
    // Single-select: each school unit represents ONE jenjang (TK/SD/SMP/SMA).
    // Clicking the currently-active chip clears it; clicking a different chip
    // replaces the previous selection wholesale, mirroring radio-button semantics.
    const gLower = g.toLowerCase();
    const active = (f.education_levels || []).includes(g);
    return {
      ...f,
      education_levels: active ? [] : [g],
      grade_levels: active ? [] : [gLower],
    };
  });

  // ===== Bulk import via CSV =====
  const downloadImportTemplate = () => {
    // Column set kept in sync with backend `admin_bulk_import_schools` in server.py.
    // Multi-value fields use "|" as separator (comma is reserved for CSV).
    const columns = [
      // Identity & location
      "name", "city", "state", "address_street", "kelurahan", "kecamatan", "latitude", "longitude",
      // Classification
      "school_type", "grade_levels", "education_levels", "curriculum", "religion_affiliation",
      "extracurriculars", "achievements",
      // Hierarchy
      "brand_id", "campus_id", "branch_name",
      // Media & content
      "description", "website", "image_url", "gallery_images", "virtual_tour_url",
      // Fees (integers, Rupiah)
      "tuition_monthly", "monthly_spp_amount", "entry_fee_amount", "other_annual_fees",
      "spp_min", "spp_max", "entry_fee_min", "entry_fee_max", "fee_academic_year",
      // Performance indicators
      "accreditation_grade", "accreditation_year", "teacher_to_student_ratio", "alumni_achievements",
      // Government / NPSN
      "npsn", "students_count", "teachers_count",
      // Subscription
      "subscription_tier",
    ];
    // Fully-populated sample row so admins can see the shape of every field.
    const sampleRow = {
      name: "TK Contoh Cerdas Bogor",
      city: "Bogor",
      state: "Jawa Barat",
      address_street: "Jl. Pajajaran No. 12",
      kelurahan: "Baranangsiang",
      kecamatan: "Bogor Timur",
      latitude: "-6.5950",
      longitude: "106.8000",
      school_type: "swasta",
      grade_levels: "tk",
      education_levels: "TK",
      curriculum: "Kurikulum Merdeka|Cambridge",
      religion_affiliation: "umum",
      extracurriculars: "Menari|Melukis|Robotika",
      achievements: "Juara 1 Menari Kota Bogor 2025",
      brand_id: "",
      campus_id: "",
      branch_name: "",
      description: "TK dengan pendekatan bermain sambil belajar, ruang kelas terang, dan rasio 1:8.",
      website: "https://contohcerdas.sch.id",
      image_url: "",
      gallery_images: "",
      virtual_tour_url: "",
      tuition_monthly: "1500000",
      monthly_spp_amount: "1500000",
      entry_fee_amount: "5000000",
      other_annual_fees: "1200000",
      spp_min: "1300000",
      spp_max: "1700000",
      entry_fee_min: "4500000",
      entry_fee_max: "5500000",
      fee_academic_year: "2026/2027",
      accreditation_grade: "A",
      accreditation_year: "2024",
      teacher_to_student_ratio: "1:8",
      alumni_achievements: "80% alumni lanjut ke SD unggulan.",
      npsn: "20220123",
      students_count: "120",
      teachers_count: "15",
      subscription_tier: "basic",
    };
    // Empty second row so admins can duplicate & fill without deleting sample.
    const emptyRow = Object.fromEntries(columns.map((c) => [c, ""]));
    const escape = (v) => {
      const s = String(v ?? "");
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const toRow = (obj) => columns.map((c) => escape(obj[c])).join(",");
    const csv = [columns.join(","), toRow(sampleRow), toRow(emptyRow)].join("\n") + "\n";
    const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url;
    a.download = "template-impor-sekolah.csv";
    document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
  };

  const submitImport = async () => {
    if (!importFile) { toast.error("Pilih file CSV terlebih dulu"); return; }
    setImportBusy(true);
    setImportResult(null);
    try {
      const fd = new FormData();
      fd.append("file", importFile);
      const r = await api.post("/admin/schools/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setImportResult(r.data);
      toast.success(`Berhasil impor ${r.data.created_count} sekolah${r.data.error_count ? `, ${r.data.error_count} baris gagal` : ""}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal mengimpor"); }
    finally { setImportBusy(false); }
  };

  // ===== Static Pages CMS handlers =====
  const openNewPage = () => { setPageForm2({ ...EMPTY_PAGE_FORM }); setEditingPage2("new"); };
  const openEditPage = (p) => { setPageForm2({ slug: p.slug, title: p.title, body: p.body, published: p.published }); setEditingPage2(p); };
  const savePage = async () => {
    if (!pageForm2.title.trim() || !pageForm2.body.trim()) { toast.error("Judul dan isi wajib diisi"); return; }
    try {
      if (editingPage2 === "new") {
        await api.post("/admin/pages", pageForm2);
        toast.success("Halaman dibuat");
      } else {
        await api.patch(`/admin/pages/${editingPage2.slug}`, pageForm2);
        toast.success("Halaman diperbarui");
      }
      setEditingPage2(null);
      setPageForm2({ ...EMPTY_PAGE_FORM });
      api.get("/admin/pages").then((r) => setStaticPages(r.data)).catch(() => {});
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal"); }
  };
  const deletePage2 = async (p) => {
    if (!window.confirm(`Hapus halaman "${p.title}" permanen?`)) return;
    try {
      await api.delete(`/admin/pages/${p.slug}`);
      toast.success("Halaman dihapus");
      api.get("/admin/pages").then((r) => setStaticPages(r.data)).catch(() => {});
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal"); }
  };

  useEffect(() => {
    if (!user) { promptLogin(nav); return; }
    if (!["admin", "superadmin", "moderator"].includes(user.role)) { nav("/"); return; }
    load();
    /* eslint-disable-next-line */
  }, [user]);

  // Auto-refresh notification counts every 60s so the red-dot badges stay live.
  useEffect(() => {
    if (!user) return;
    const timer = setInterval(() => {
      api.get("/admin/notification-counts")
        .then((r) => setNotifCounts(r.data))
        .catch(() => {});
    }, 60000);
    return () => clearInterval(timer);
  }, [user]);

  // "Tandai semua sudah dibaca" — POST ack timestamp, then optimistically zero
  // all three notif counts so the badges disappear immediately.
  const ackNotifCounts = async () => {
    try {
      await api.post("/admin/notification-counts/ack");
      setNotifCounts({ pending_reviews: 0, new_users_24h: 0, new_community_since_login: 0 });
      toast.success("Semua notifikasi ditandai sudah dibaca");
    } catch (err) {
      toast.error("Gagal menandai notifikasi");
    }
  };

  // Auto-save draft every 30s while composer is open
  useEffect(() => {
    if (!newPostOpen && !editingPost) return;
    if (!(postForm.title || postForm.body || postForm.excerpt)) return;
    const timer = setInterval(async () => {
      try {
        setDraftSaving(true);
        const payload = { slug: editingPost?.slug || null, ...postForm };
        const res = await api.put("/blog-drafts", payload);
        setDraftSavedAt(res.data?.saved_at || new Date().toISOString());
      } catch {}
      finally { setDraftSaving(false); }
    }, 30000);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newPostOpen, editingPost, postForm.title, postForm.body, postForm.excerpt, postForm.cover_url, postForm.tags]);


  // Iter153 — inline edit for suggestion cards.
  const [editingSid, setEditingSid] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [editSaving, setEditSaving] = useState(false);
  const openEdit = (s) => {
    const d = s.submitted_data || {};
    setEditingSid(s.suggestion_id);
    setEditForm({
      name: d.name || s.school_name || "",
      city: d.city || "",
      state: d.state || "",
      school_type: d.school_type || "negeri",
      grade_levels: d.grade_levels || [],
      curriculum: d.curriculum || [],
      description: d.description || "",
      website: d.website || "",
      image_url: d.image_url || "",
      brand_id: d.brand_id || "",
      branch_name: d.branch_name || "",
      entry_fee_amount: d.entry_fee_amount ?? "",
      monthly_spp_amount: d.monthly_spp_amount ?? "",
      accreditation_grade: d.accreditation_grade || "",
    });
  };
  const cancelEdit = () => { setEditingSid(null); setEditForm({}); };
  const saveEdit = async (sid) => {
    setEditSaving(true);
    try {
      const nn = (v) => (v === "" || v == null ? null : Number(v));
      await api.patch(`/admin/schools/suggestions/${sid}`, {
        ...editForm,
        entry_fee_amount: nn(editForm.entry_fee_amount),
        monthly_spp_amount: nn(editForm.monthly_spp_amount),
        accreditation_grade: editForm.accreditation_grade || null,
      });
      toast.success("Perubahan tersimpan");
      cancelEdit();
      load();
    } catch (e) {
      const d = e?.response?.data?.detail;
      const msg = typeof d === "string" ? d : Array.isArray(d) ? d.map((x) => x?.msg).filter(Boolean).join(" · ") : "Gagal menyimpan";
      toast.error(msg);
    } finally { setEditSaving(false); }
  };
  const toggleEditGrade = (g) => setEditForm((prev) => ({
    ...prev,
    grade_levels: (prev.grade_levels || []).includes(g)
      ? prev.grade_levels.filter((x) => x !== g)
      : [...(prev.grade_levels || []), g],
  }));
  const toggleEditCurriculum = (c) => setEditForm((prev) => ({
    ...prev,
    curriculum: (prev.curriculum || []).includes(c)
      ? prev.curriculum.filter((x) => x !== c)
      : [...(prev.curriculum || []), c],
  }));

  // Iter149 — Two-step approve flow for school suggestions.
  const reviewSuggestion = async (sid) => {
    try { await api.post(`/admin/schools/suggestions/${sid}/review`); toast.success("Ditandai sedang direview"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Gagal"); }
  };
  const publishSuggestion = async (sid) => {
    try {
      const r = await api.post(`/admin/schools/suggestions/${sid}/publish`, {});
      toast.success("Sekolah dipublikasi ke direktori");
      load();
      return r.data;
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal publish"); }
  };
  const markDuplicateSuggestion = async (sid) => {
    const target = window.prompt("Masukkan school_id sekolah yang sudah ada (target duplikat):");
    if (!target || !target.trim()) return;
    try { await api.post(`/admin/schools/suggestions/${sid}/duplicate`, { of_school_id: target.trim() }); toast.success("Ditandai duplikat"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Gagal"); }
  };
  const rejectSuggestion = async (sid) => {
    const reason = window.prompt("Alasan penolakan (opsional):") || "";
    if (reason === null) return;
    try { await api.post(`/admin/schools/suggestions/${sid}/reject`, { reason }); toast.success("Usulan ditolak"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Gagal"); }
  };
  const approveSchool = async (id) => {
    try { await api.post(`/admin/schools/${id}/approve`); toast.success("Sekolah disetujui"); load(); }
    catch { toast.error("Gagal"); }
  };
  const deleteSchool = async (id) => {
    if (!window.confirm("Hapus sekolah beserta semua review & wishlist? Aksi permanen.")) return;
    try { await api.delete(`/admin/schools/${id}`); toast.success("Sekolah dihapus"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Gagal"); }
  };
  const duplicateSchool = async (s) => {
    if (!window.confirm(`Duplikat "${s.name}"?\nSemua data akan disalin kecuali NPSN, foto, dan review. Klon akan diberi nama "${s.name} (Salinan)" — silakan edit setelahnya untuk mengganti jenjang.`)) return;
    try {
      const r = await api.post(`/admin/schools/${s.school_id}/duplicate`);
      toast.success(`Duplikat dibuat — silakan edit untuk mengganti jenjang & nama`);
      await load();
      // Open Edit dialog on the newly created clone so admin can immediately rename it
      if (r.data?.school_id) setEditingSchool(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal duplikat"); }
  };
  const saveSchoolEdits = async () => {
    try {
      const { school_id, avg_rating, review_count, ai_summary, verified_ratio, verified_reviews, trusted, created_at, status, submitted_by, ...payload } = editingSchool;
      // Single-select jenjang: mirror the chosen level between education_levels
      // (uppercase, for display badges) and grade_levels (lowercase, for the
      // public filter). Prefer whichever array the admin just interacted with;
      // if only one is set (legacy row), backfill the other from it.
      const edu = Array.isArray(payload.education_levels) ? payload.education_levels.slice(0, 1) : [];
      const grade = Array.isArray(payload.grade_levels) ? payload.grade_levels.slice(0, 1) : [];
      const canonicalUpper = edu[0] || (grade[0] ? grade[0].toUpperCase() : null);
      payload.education_levels = canonicalUpper ? [canonicalUpper] : [];
      payload.grade_levels = canonicalUpper ? [canonicalUpper.toLowerCase()] : [];
      await api.put(`/admin/schools/${school_id}`, payload);
      toast.success("Perubahan disimpan"); setEditingSchool(null);
      setHierarchyRefreshKey((k) => k + 1);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal"); }
  };
  const decideVerif = async (verifId, decision) => {
    try { await api.post(`/admin/verifications/${verifId}/decide`, { decision }); toast.success(decision === "approved" ? "Verifikasi disetujui" : "Ditolak"); load(); }
    catch { toast.error("Gagal"); }
  };
  const deleteReview = async (rid) => {
    if (!window.confirm("Hapus review ini?")) return;
    try { await api.delete(`/admin/reviews/${rid}`); toast.success("Review dihapus"); load(); }
    catch { toast.error("Gagal"); }
  };
  const changeRole = async (uid, role) => {
    try { await api.patch(`/admin/users/${uid}/role`, { role }); toast.success("Peran diubah"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Gagal"); }
  };
  const toggleFoundingParent = async (u) => {
    const next = !u.is_founding_parent;
    if (!window.confirm(next
      ? `Jadikan ${u.name} sebagai Founding Parent? Lencana emas akan langsung tampil di semua review & thread mereka.`
      : `Cabut status Founding Parent dari ${u.name}?`)) return;
    try {
      await api.patch(`/admin/users/${u.user_id}/founding-parent`, { is_founding_parent: next });
      toast.success(next ? "Status Founding Parent diberikan" : "Status Founding Parent dicabut");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal"); }
  };
  const deleteAdmin = async (uid) => {
    // Guard: the delete route requires the full user_id (with "user_" prefix).
    // Reject silently if the caller passed something else — prevents accidental
    // /admin/users/undefined or /admin/users/1 requests.
    if (typeof uid !== "string" || !uid.startsWith("user_")) {
      toast.error("ID pengguna tidak valid");
      return;
    }
    if (!window.confirm("Hapus akun admin ini?")) return;
    try {
      await api.delete(`/admin/users/${uid}`);
      toast.success("Admin dihapus");
      load();
    } catch (e) {
      // 404 = row already deleted server-side (e.g. two tabs open, or previous
      // request succeeded but network response was lost). Just drop the row
      // from the local lists so the UI catches up.
      if (e.response?.status === 404) {
        setAdmins((prev) => prev.filter((a) => a.user_id !== uid));
        setAllUsers((prev) => prev.filter((u) => u.user_id !== uid));
        toast.success("Baris pengguna dibersihkan (data sudah tidak ada di server)");
        return;
      }
      toast.error(e.response?.data?.detail || "Gagal");
    }
  };
  const createAdmin = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/users", newAdmin);
      toast.success("Admin dibuat"); setNewAdminOpen(false);
      setNewAdmin({ email: "", password: "", name: "", role: "admin" });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal"); }
  };
  const downloadCsv = async () => {
    try {
      const r = await api.get("/admin/verifications/stats.csv", { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = url;
      a.download = `verification-stats-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch { toast.error("Gagal mengunduh CSV"); }
  };

  const downloadReviewsCsv = async () => {
    try {
      const r = await api.get("/admin/reviews.csv", { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = url;
      a.download = `reviews-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
      toast.success("CSV review diunduh");
    } catch { toast.error("Gagal mengunduh CSV review"); }
  };

  // ===== Community management handlers =====
  const catLabel = (slug) => forumCats.find((c) => c.slug === slug)?.label || slug;

  const saveCategory = async (mode) => {
    try {
      if (mode === "create") {
        await api.post("/admin/forum/categories", catForm);
        toast.success("Kategori dibuat");
      } else {
        await api.patch(`/admin/forum/categories/${editingCat.category_id}`, {
          label: catForm.label, description: catForm.description, order: Number(catForm.order),
        });
        toast.success("Kategori diperbarui");
      }
      setNewCatOpen(false); setEditingCat(null); setCatForm(emptyCat);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal"); }
  };
  const openEditCat = (c) => { setEditingCat(c); setCatForm({ slug: c.slug, label: c.label, description: c.description || "", order: c.order }); };
  const deleteCategory = async (c) => {
    const reassign = window.prompt(`Hapus kategori "${c.label}". Thread yang ada akan dipindah ke kategori (slug):`, "umum");
    if (!reassign) return;
    try {
      await api.delete(`/admin/forum/categories/${c.category_id}`, { params: { reassign_to: reassign.trim() } });
      toast.success("Kategori dihapus");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal"); }
  };
  const deleteThread = async (t) => {
    if (!window.confirm(`Hapus thread "${t.title}" beserta semua balasannya? Aksi permanen.`)) return;
    try { await api.delete(`/admin/threads/${t.thread_id}`); toast.success("Thread dihapus"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Gagal"); }
  };
  const togglePin = async (t) => {
    try {
      await api.patch(`/admin/threads/${t.thread_id}/pin`, { pinned: !t.pinned });
      toast.success(t.pinned ? "Pin dilepas" : "Thread dipinkan ke atas");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal"); }
  };
  const deleteReplyMod = async (r) => {
    if (!window.confirm("Hapus balasan ini?")) return;
    try { await api.delete(`/admin/replies/${r.reply_id}`); toast.success("Balasan dihapus"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Gagal"); }
  };

  // ===== User Management (ban/unban) =====
  const openBanDialog = (u) => {
    setBanDialogUser(u);
    setBanForm({ reason: "", duration_days: 0 });
  };
  const submitBan = async () => {
    if (!banDialogUser) return;
    try {
      await api.patch(`/admin/users/${banDialogUser.user_id}/ban`, {
        banned: true,
        reason: banForm.reason,
        duration_days: Number(banForm.duration_days) || 0,
      });
      toast.success(banForm.duration_days ? `Akun dinonaktifkan ${banForm.duration_days} hari` : "Akun dinonaktifkan permanen");
      setBanDialogUser(null);
      setBanForm({ reason: "", duration_days: 0 });
      loadAllUsers();
      loadAdminActions();
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal"); }
  };
  const unbanUser = async (u) => {
    if (!window.confirm(`Aktifkan kembali akun ${u.email}?`)) return;
    try {
      await api.patch(`/admin/users/${u.user_id}/ban`, { banned: false });
      toast.success("Akun diaktifkan kembali");
      loadAllUsers();
      loadAdminActions();
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal"); }
  };

  if (!user || !["admin", "superadmin", "moderator"].includes(user.role)) return null;

  const loadModApps = () => {
    const params = { status: modAppsStatus, limit: 200 };
    return api.get("/admin/moderator-applications", { params }).then((r) => setModApps(r.data)).catch(() => {});
  };

  const reviewModApp = async (action) => {
    if (!reviewingApp) return;
    try {
      const res = await api.patch(`/admin/moderator-applications/${reviewingApp.application_id}`, { action, note: reviewNote });
      if (res.data?.promoted_user_id) toast.success("Aplikasi disetujui — user dipromosikan menjadi moderator");
      else if (action === "approve") toast.success("Aplikasi disetujui (akun belum terdaftar; user diminta buat akun terlebih dahulu)");
      else toast.success("Aplikasi ditolak");
      setReviewingApp(null);
      setReviewNote("");
      loadModApps();
      loadAdminActions && loadAdminActions();
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal"); }
  };

  const savePost = async (mode, statusOverride) => {
    // Client-side guardrail: matches backend rule on CREATE and enforces the
    // same intent for UPDATE too — an editor should never *publish* an article
    // that has no category, even if the record originated as a legacy import.
    // We deliberately gate BOTH Draft and Publish here so autosave/round-trips
    // don't produce untaxonomised content going forward.
    if (mode === "create" && !(postForm.categories || []).length) {
      toast.error("Pilih minimal 1 kategori sebelum menyimpan artikel.");
      return;
    }
    if (mode === "edit" && !(postForm.categories || []).length) {
      toast.error("Artikel ini belum dikategorikan. Pilih minimal 1 kategori terlebih dahulu.");
      return;
    }
    try {
      const payload = statusOverride ? { ...postForm, status: statusOverride } : postForm;
      if (mode === "create") await api.post("/blog", payload);
      else await api.put(`/blog/${editingPost.slug}`, payload);
      // Delete draft after publishing
      try {
        const params = editingPost?.slug ? { slug: editingPost.slug } : {};
        await api.delete("/blog-drafts", { params });
      } catch {}
      const finalStatus = payload.status || postForm.status || "published";
      const msg = finalStatus === "draft"
        ? (mode === "create" ? "Draf disimpan" : "Perubahan draf disimpan")
        : (mode === "create" ? "Artikel diterbitkan" : "Artikel diperbarui");
      toast.success(msg);
      setNewPostOpen(false); setEditingPost(null); setPostForm(emptyPost);
      setDraftSavedAt(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal"); }
  };

  // Preview the article in a new tab exactly as it will look when published.
  const previewPost = async () => {
    if (!(postForm.title || "").trim() || !(postForm.body || "").trim()) {
      toast.error("Isi judul dan bodi artikel terlebih dahulu untuk pratinjau");
      return;
    }
    // Preview = admin-only server snapshot of the CURRENT form (no save, no
    // status change), rendered at /pratinjau/:id with the public article
    // layout. Not under /jurnal/ so no static fallback/redirect can hijack it.
    // The tab is opened synchronously (popup blockers), then pointed at the URL.
    const tab = window.open("", "_blank");
    if (tab) tab.opener = null;
    try {
      const res = await api.post("/admin/blog-previews", { ...postForm, post_id: editingPost?.post_id || null });
      const url = res.data?.url;
      if (!url) throw new Error("URL pratinjau tidak diterima dari server");
      if (tab) tab.location.href = url;
      else window.open(url, "_blank", "noopener,noreferrer");
      toast.success("Pratinjau dibuka di tab baru");
    } catch (e) {
      if (tab) tab.close();
      toast.error(e.response?.data?.detail || "Gagal membuka pratinjau");
    }
  };

  const deletePost = async (slug) => {
    if (!window.confirm("Hapus artikel ini?")) return;
    try { await api.delete(`/blog/${slug}`); toast.success("Artikel dihapus"); load(); }
    catch { toast.error("Gagal"); }
  };
  const openEditPost = (p) => {
    setEditingPost(p);
    setPostForm({
      title: p.title, excerpt: p.excerpt, body: p.body,
      cover_url: p.cover_url || "", tags: p.tags || [],
      // Hydrate multi-select taxonomy. Legacy articles ship `categories: []`
      // — the composer surfaces a "Belum dikategorikan" hint and the client
      // validation below prevents Save/Publish until the editor picks ≥1.
      categories: Array.isArray(p.categories) ? p.categories.slice() : [],
      // Backend exposes the admin alias `school_ids` alongside canonical
      // `related_school_ids`; prefer the alias, fall back for older payloads.
      school_ids: Array.isArray(p.school_ids)
        ? p.school_ids.slice()
        : Array.isArray(p.related_school_ids)
          ? p.related_school_ids.slice()
          : [],
      content_type: p.content_type || "insight",
      is_featured: !!p.is_featured,
      key_stats: p.key_stats || [],
      pdf_url: p.pdf_url || "",
      pdf_size_bytes: p.pdf_size_bytes || null,
      author_title: p.author_title || "",
      author_avatar_url: p.author_avatar_url || "",
      read_time_min: p.read_time_min || null,
      takeaway: p.takeaway || "",
      slug: p.slug || "",
      status: p.status || "published",
    });
    setSchoolPickerQuery("");
    // Try to hydrate the newer draft (if user had unsaved changes) after opening.
    // Server-first policy — the published article is the source of truth. We
    // only prompt to restore a draft if it's STRICTLY newer than the published
    // updated_at, has real content, AND the timestamps are actually valid.
    // Once the user makes a decision (OK or Cancel), the draft is always
    // deleted so this prompt never repeats for the same article.
    api.get("/blog-drafts", { params: { slug: p.slug } }).then((r) => {
      const d = r.data;
      if (!d || !d.updated_at) return;
      const draftTs = new Date(d.updated_at).getTime();
      const publishedTs = new Date(p.updated_at || p.created_at || 0).getTime();
      const hasContent = !!(d.title || d.body);
      if (!hasContent || !Number.isFinite(draftTs) || draftTs <= publishedTs) {
        // Draft is older/equal to server → just clean it up silently.
        api.delete("/blog-drafts", { params: { slug: p.slug } }).catch(() => {});
        return;
      }
      const load = window.confirm("Ditemukan draft yang lebih baru untuk artikel ini. Muat draft?");
      if (load) {
        // Merge with the already-hydrated form so we don't obliterate fields
        // (e.g. `takeaway`, `read_time_min`) that the draft doesn't override.
        setPostForm((prev) => ({
          ...prev,
          title: d.title ?? prev.title,
          excerpt: d.excerpt ?? prev.excerpt,
          body: d.body ?? prev.body,
          cover_url: d.cover_url ?? prev.cover_url,
          tags: d.tags ?? prev.tags,
        }));
        setDraftSavedAt(d.updated_at);
      }
      // Either way — clear the draft so the prompt never repeats. If user
      // chose to load, autosave will re-create it on next keystroke.
      api.delete("/blog-drafts", { params: { slug: p.slug } }).catch(() => {});
    }).catch(() => {});
  };

  // Restore latest 'new' draft when opening the composer
  const openNewPost = () => {
    setNewPostOpen(true);
    setEditingPost(null);
    setPostForm(emptyPost);
    setDraftSavedAt(null);
    api.get("/blog-drafts").then((r) => {
      const d = r.data;
      if (!d || !(d.title || d.body)) return;
      const load = window.confirm("Ditemukan draft artikel yang belum diterbitkan. Lanjutkan menulis?");
      if (load) {
        setPostForm({
          ...emptyPost,
          title: d.title || "", excerpt: d.excerpt || "", body: d.body || "",
          cover_url: d.cover_url || "", tags: d.tags || [],
        });
        setDraftSavedAt(d.updated_at);
      }
      // Always delete the current draft — if user loads, autosave re-creates
      // it on the next keystroke; if user cancels, we don't want the prompt
      // to reappear next time the composer opens.
      api.delete("/blog-drafts").catch(() => {});
    }).catch(() => {});
  };


  return (
    <div data-testid="admin-page" className="max-w-6xl mx-auto px-6 lg:px-10 py-12">
      <div className="text-xs uppercase tracking-widest text-[#4B5563] inline-flex items-center gap-2">
        Editorial · <span className={"inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] " + (isSuper ? "bg-[#1E3A8A] text-white" : isModerator ? "bg-[#E07A5F] text-white" : "bg-[#EAE7E0] text-[#1F2937]")}>
          <Shield size={10} /> {isSuper ? "SUPER ADMIN" : isModerator ? "MODERATOR" : "ADMIN"}
        </span>
      </div>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <h1 className="font-serif-display text-4xl lg:text-5xl text-[#1F2937] mt-2">Dasbor editor</h1>
        {isModOrAbove && (
          <a href="/admin/alumni-analytics" data-testid="admin-alumni-analytics-link"
            className="mt-4 inline-flex items-center gap-2 h-10 px-4 rounded-full bg-[#7C3AED] hover:bg-[#6D28D9] text-white text-sm font-medium transition-colors">
            <GraduationCap size={14} /> Analitik Alumni
          </a>
        )}
      </div>

      <Tabs value={activeTab || (isModerator ? "reviews-mgmt" : "schools-mgmt")} onValueChange={setActiveTab} className="mt-8">
        {/* Notification acknowledgement — small link-style button that only appears
            when at least one red-dot badge is active. Zeroes all three at once. */}
        {(notifCounts.pending_reviews + notifCounts.new_users_24h + notifCounts.new_community_since_login) > 0 && (
          <div className="flex justify-end mb-2">
            <button
              type="button"
              onClick={ackNotifCounts}
              data-testid="ack-notifications-btn"
              className="inline-flex items-center gap-1.5 text-xs text-[#4B5563] hover:text-[#1E3A8A] font-medium transition-colors"
              title="Tandai semua notifikasi tab sebagai sudah dibaca"
            >
              <Check size={12} strokeWidth={2.25} /> Tandai semua sudah dibaca
            </button>
          </div>
        )}
        <TabsList data-testid="admin-tabs" className="flex-wrap h-auto">
          {isAdminOrSuper && <TabsTrigger value="schools-mgmt" data-testid="tab-schools-mgmt">Kelola sekolah</TabsTrigger>}
          {isAdminOrSuper && <TabsTrigger value="hierarchy" data-testid="tab-hierarchy"><Users size={12} className="mr-1" /> Grup &amp; Cabang</TabsTrigger>}
          <TabsTrigger value="reviews-mgmt" data-testid="tab-reviews-mgmt">Review masuk {notifCounts.pending_reviews > 0 && <span data-testid="badge-pending-reviews" className="ml-1.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-[#DC2626] text-white text-[10px]">{notifCounts.pending_reviews}</span>}</TabsTrigger>
          {isAdminOrSuper && <TabsTrigger value="blog-cms" data-testid="tab-blog-cms">Artikel jurnal</TabsTrigger>}
          {isModOrAbove && <TabsTrigger value="article-discussions" data-testid="tab-article-discussions"><MessagesSquare size={12} className="mr-1" /> Diskusi Artikel</TabsTrigger>}
          {isAdminOrSuper && <TabsTrigger value="verifs" data-testid="tab-verifs">Verifikasi {verifs.length > 0 && <span className="ml-1.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-[#E07A5F] text-white text-[10px]">{verifs.length}</span>}</TabsTrigger>}
          {isAdminOrSuper && <TabsTrigger value="pending-schools" data-testid="tab-pending-schools">Usulan {pending.length > 0 && <span className="ml-1.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-[#E07A5F] text-white text-[10px]">{pending.length}</span>}</TabsTrigger>}
          {isModOrAbove && <TabsTrigger value="community" data-testid="tab-community"><MessagesSquare size={12} className="mr-1" /> Ruang & Diskusi {notifCounts.new_community_since_login > 0 && <span data-testid="badge-new-community" className="ml-1.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-[#DC2626] text-white text-[10px]">{notifCounts.new_community_since_login}</span>}</TabsTrigger>}
          {isSuper && <TabsTrigger value="forum-console" data-testid="tab-forum-console"><MessagesSquare size={12} className="mr-1" /> Legacy Mapping</TabsTrigger>}
          {isModOrAbove && <TabsTrigger value="photos" data-testid="tab-photos"><ImageIcon size={12} className="mr-1" /> Foto</TabsTrigger>}
          {isModOrAbove && <TabsTrigger value="photo-reports" data-testid="tab-photo-reports"><ImageIcon size={12} className="mr-1" /> Laporan foto {photoReports.filter((r) => r.status === "open").length > 0 && <span className="ml-1.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-[#DC2626] text-white text-[10px]">{photoReports.filter((r) => r.status === "open").length}</span>}</TabsTrigger>}
          {isModOrAbove && <TabsTrigger value="review-reports" data-testid="tab-review-reports"><Flag size={12} className="mr-1" /> Laporan review {reviewReports.filter((r) => r.status === "open").length > 0 && <span className="ml-1.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-[#DC2626] text-white text-[10px]">{reviewReports.filter((r) => r.status === "open").length}</span>}</TabsTrigger>}
          {isModOrAbove && <TabsTrigger value="forum-reports" data-testid="tab-forum-reports"><Flag size={12} className="mr-1" /> Laporan Forum {forumReports.filter((r) => r.status === "open").length > 0 && <span className="ml-1.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-[#DC2626] text-white text-[10px]">{forumReports.filter((r) => r.status === "open").length}</span>}</TabsTrigger>}
          {isAdminOrSuper && <TabsTrigger value="pages" data-testid="tab-pages"><FileText size={12} className="mr-1" /> Halaman</TabsTrigger>}
          {isAdminOrSuper && <TabsTrigger value="stats" data-testid="tab-stats">Statistik</TabsTrigger>}
          {isSuper && <TabsTrigger value="users" data-testid="tab-users">Akun admin</TabsTrigger>}
          {isSuper && <TabsTrigger value="all-users" data-testid="tab-all-users"><Users size={12} className="mr-1" /> Pengguna {notifCounts.new_users_24h > 0 && <span data-testid="badge-new-users" className="ml-1.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-[#DC2626] text-white text-[10px]">{notifCounts.new_users_24h}</span>}</TabsTrigger>}
          {isSuper && <TabsTrigger value="gamification" data-testid="tab-gamification"><Trophy size={12} className="mr-1" /> Level</TabsTrigger>}
          {isSuper && <TabsTrigger value="mod-apps" data-testid="tab-mod-apps"><Shield size={12} className="mr-1" /> Aplikasi Moderator {modApps.filter((a) => a.status === "pending").length > 0 && <span className="ml-1.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-[#DC2626] text-white text-[10px]">{modApps.filter((a) => a.status === "pending").length}</span>}</TabsTrigger>}
          {isSuper && <TabsTrigger value="audit-log" data-testid="tab-audit-log"><BarChart3 size={12} className="mr-1" /> Log Aksi</TabsTrigger>}
        </TabsList>

        {/* ===== KELOLA SEKOLAH (CRUD) ===== */}
        <SchoolsTab
          schools={filteredSchools}
          totalCount={schoolsTotal}
          isAdminOrSuper={isAdminOrSuper}
          isSuper={isSuper}
          onImport={() => { setImportResult(null); setImportFile(null); setImportOpen(true); }}
          onNewSchool={() => setNewSchoolOpen(true)}
          onEdit={setEditingSchool}
          onDuplicate={duplicateSchool}
          onDelete={deleteSchool}
          query={schoolQuery}
          onQueryChange={setSchoolQuery}
          provinceFilter={schoolProvinceFilter}
          onProvinceFilterChange={setSchoolProvinceFilter}
          levelFilter={schoolLevelFilter}
          onLevelFilterChange={setSchoolLevelFilter}
          typeFilter={schoolTypeFilter}
          onTypeFilterChange={setSchoolTypeFilter}
        />

        {/* ===== HIERARCHY (Group → Campus → Unit tree) ===== */}
        <HierarchyTab
          isAdminOrSuper={isAdminOrSuper}
          refreshKey={hierarchyRefreshKey}
          onEditSchool={async (schoolId) => {
            try {
              const r = await api.get(`/schools/${schoolId}`);
              setEditingSchool(r.data);
            } catch (e) {
              toast.error(e.response?.data?.detail || "Gagal memuat sekolah");
            }
          }}
          onAddUnit={(campus) => {
            // Pre-fill the "Tambah sekolah" dialog with the campus context so the new unit
            // lands under the right campus/city on save. Everything else the admin can fill inline.
            setNewSchoolForm({
              ...emptyNewSchool,
              campus_id: campus.campus_id || null,
              city: campus.city || "",
              state: campus.state || "",
              brand_id: campus.slug || campus.campus_id || "",
            });
            setNewSchoolPhotos([]);
            setNewSchoolOpen(true);
          }}
        />

        {/* ===== REVIEW MODERATION (Post-moderation) ===== */}
        <ReviewsTab
          reviews={reviews}
          isAdminOrSuper={isAdminOrSuper}
          reviewVerFilter={reviewVerFilter}
          setReviewVerFilter={setReviewVerFilter}
          reviewRoleFilter={reviewRoleFilter}
          setReviewRoleFilter={setReviewRoleFilter}
          reviewGradMin={reviewGradMin}
          setReviewGradMin={setReviewGradMin}
          reviewGradMax={reviewGradMax}
          setReviewGradMax={setReviewGradMax}
          reviewEliteOnly={reviewEliteOnly}
          setReviewEliteOnly={setReviewEliteOnly}
          loadReviews={loadReviews}
          downloadReviewsCsv={downloadReviewsCsv}
          verifyReview={verifyReview}
          deleteReview={deleteReview}
        />

        {/* ===== ARTIKEL JURNAL (CMS) ===== */}
        {isAdminOrSuper && (
          <TabsContent value="blog-cms" className="mt-6 space-y-3" data-testid="blog-cms-panel">
            <div className="flex justify-between items-center gap-3 flex-wrap">
              <p className="text-sm text-[#4B5563]">Kelola artikel editorial The Reports. Sebagai admin, Anda bisa menulis, mengedit, dan menghapus artikel.</p>
              <div className="flex items-center gap-2">
                <label className="text-[10px] uppercase tracking-widest text-[#4B5563] font-bold" htmlFor="blog-category-filter">Filter kategori</label>
                <select
                  id="blog-category-filter"
                  data-testid="blog-category-filter"
                  value={blogCategoryFilter}
                  onChange={(e) => setBlogCategoryFilter(e.target.value)}
                  className="h-9 px-3 rounded-md border border-[#EAE7E0] bg-white text-sm"
                >
                  <option value="">Semua kategori</option>
                  <option value="__uncategorized__">Belum dikategorikan</option>
                  {JOURNAL_CATEGORIES.map((c) => (
                    <option key={c.slug} value={c.slug}>{c.label}</option>
                  ))}
                </select>
                <Button onClick={openNewPost} data-testid="new-post-btn" className="rounded-full bg-[#E07A5F] hover:bg-[#C96D55] text-white">
                  <Plus size={14} className="mr-1" /> Tulis artikel
                </Button>
              </div>
            </div>
            {(() => {
              const filtered = blogPosts.filter((p) => {
                if (!blogCategoryFilter) return true;
                const cats = Array.isArray(p.categories) ? p.categories : [];
                if (blogCategoryFilter === "__uncategorized__") return cats.length === 0;
                // Multi-category articles match if ANY of their slugs equals the filter.
                return cats.includes(blogCategoryFilter);
              });
              if (filtered.length === 0) {
                return (
                  <div data-testid="blog-list-empty" className="text-[#4B5563] text-sm p-4">
                    {blogPosts.length === 0
                      ? "Belum ada artikel. Klik \"Tulis artikel\" untuk memulai."
                      : "Tidak ada artikel pada filter kategori ini."}
                  </div>
                );
              }
              return filtered.map((p, i) => {
                const isDraft = (p.status || "published") === "draft";
                const cats = Array.isArray(p.categories) ? p.categories : [];
                return (
                  <div key={p.post_id} data-testid={`post-row-${i}`} className={`bg-white border rounded-2xl p-4 flex items-start gap-4 ${isDraft ? "border-[#F1C97A] bg-[#FFFBEE]" : "border-[#EAE7E0]"}`}>
                    {p.cover_url && <img src={p.cover_url} alt="" className="w-24 h-16 rounded-xl object-cover" />}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span
                          data-testid={`post-status-badge-${i}`}
                          className={`inline-flex items-center gap-1 rounded-full text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 ${isDraft ? "bg-[#F5D889] text-[#7A4F00] border border-[#E6B754]" : "bg-[#D9EAD3] text-[#1F5B2B] border border-[#A9CB9C]"}`}
                        >
                          <span className={`w-1.5 h-1.5 rounded-full ${isDraft ? "bg-[#B0781F]" : "bg-[#2E7D32]"}`} aria-hidden="true" />
                          {isDraft ? "Draf" : "Tayang"}
                        </span>
                        <div className="text-xs uppercase tracking-widest text-[#4B5563]">{p.tags?.join(" · ") || "Editorial"} · oleh {p.author_name} · {new Date(p.created_at).toLocaleDateString("id-ID")}</div>
                      </div>
                      <div className="flex items-center gap-1.5 flex-wrap mt-1.5" data-testid={`post-categories-${i}`}>
                        {cats.length === 0 ? (
                          <span className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] uppercase tracking-widest font-bold bg-[#F3F1ED] text-[#8A6A00] border border-[#E6D28A]">Belum dikategorikan</span>
                        ) : (
                          cats.map((slug) => (
                            <span key={slug} className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] uppercase tracking-widest font-bold bg-[#EEF2FF] text-[#1E3A8A] border border-[#C7D2FE]">
                              {JOURNAL_CATEGORY_LABEL[slug] || slug}
                            </span>
                          ))
                        )}
                      </div>
                      <div className="font-serif-display text-lg text-[#1F2937] mt-1">{p.title}</div>
                      <div className="text-sm text-[#4B5563] mt-1 line-clamp-1">{p.excerpt}</div>
                    </div>
                    <Button variant="outline" onClick={() => openEditPost(p)} data-testid={`edit-post-${i}`} className="rounded-full border-[#EAE7E0]">
                      <Pencil size={14} className="mr-1" /> Edit
                    </Button>
                    <Button variant="outline" onClick={() => deletePost(p.slug)} data-testid={`delete-post-${i}`} className="rounded-full border-[#EAE7E0] text-[#B91C1C]">
                      <Trash2 size={14} />
                    </Button>
                  </div>
                );
              });
            })()}
          </TabsContent>
        )}

        {/* ===== VERIFIKASI ===== */}
        <TabsContent value="verifs" className="mt-6 space-y-3">
          {verifs.length === 0 && <div className="text-[#4B5563]">Tidak ada permintaan verifikasi yang menunggu.</div>}
          {verifs.map((v, i) => (
            <div key={v.verif_id} data-testid={`verif-${i}`} className="bg-white border border-[#EAE7E0] rounded-2xl p-5 grid md:grid-cols-[220px_1fr_auto] gap-4">
              <button type="button" onClick={() => v.proof_image_b64 && setPreviewImg(v.proof_image_b64)} className="rounded-xl overflow-hidden border border-[#EAE7E0] bg-[#F3F1ED] aspect-[4/3] flex items-center justify-center text-xs text-[#4B5563]">
                {v.proof_image_b64 ? <img src={v.proof_image_b64} alt="bukti" className="w-full h-full object-cover" /> : <span>Tanpa bukti</span>}
              </button>
              <div className="min-w-0">
                <div className="text-xs uppercase tracking-widest text-[#4B5563]">{v.school_name} · {v.child_grade}{v.proof_kind ? ` · ${PROOF_LABEL[v.proof_kind] || v.proof_kind}` : ""}</div>
                <div className="font-serif-display text-lg text-[#1F2937] mt-1">{v.user_name}</div>
                <div className="text-xs text-[#4B5563]">{v.user_email}</div>
                {v.note && <div className="mt-2 text-sm text-[#4B5563] italic">"{v.note}"</div>}
              </div>
              <div className="flex md:flex-col gap-2">
                <Button onClick={() => decideVerif(v.verif_id, "approved")} className="rounded-full bg-[#166534] hover:bg-[#14532D] text-white"><BadgeCheck size={14} className="mr-1" /> Setujui</Button>
                <Button onClick={() => decideVerif(v.verif_id, "rejected")} variant="outline" className="rounded-full border-[#EAE7E0] text-[#B91C1C]"><X size={14} className="mr-1" /> Tolak</Button>
              </div>
            </div>
          ))}
        </TabsContent>

        {/* ===== USULAN SEKOLAH ===== */}
        <TabsContent value="pending-schools" className="mt-6 space-y-3">
          {pending.length === 0 && <div className="text-[#4B5563]">Tidak ada usulan sekolah yang menunggu.</div>}
          {pending.map((s) => {
            const submitted = (s.submitted_data || {});
            const sid = s.suggestion_id;
            const status = s.status || "submitted";
            const isTerminal = status === "approved" || status === "duplicate" || status === "rejected";
            const badgeStyle = status === "approved" ? "bg-[#DCFCE7] text-[#166534]"
              : status === "reviewing" ? "bg-[#FEF3C7] text-[#92400E]"
              : status === "duplicate" ? "bg-[#E0E7FF] text-[#3730A3]"
              : status === "rejected"  ? "bg-[#FEE2E2] text-[#991B1B]"
              : "bg-[#F3F4F6] text-[#374151]";
            return (
              <div key={sid} data-testid={`suggestion-${sid}`}
                   className="bg-white border border-[#EAE7E0] rounded-2xl p-5">
                <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] uppercase tracking-widest font-semibold ${badgeStyle}`}
                            data-testid={`suggestion-${sid}-status`}>
                        {status}
                      </span>
                      <span className="text-xs text-[#4B5563]">
                        {submitted.city || "—"}{submitted.state ? `, ${submitted.state}` : ""} · {submitted.school_type || "—"}
                      </span>
                    </div>
                    <div className="font-serif-display text-lg text-[#1F2937] mt-1">{s.school_name || submitted.name}</div>
                    <div className="text-[11px] text-[#8A8F99]">oleh {s.email || s.user_id} · {new Date(s.created_at).toLocaleString("id-ID")}</div>
                    {submitted.description && editingSid !== sid && (
                      <div className="text-sm text-[#4B5563] mt-2 line-clamp-3">{submitted.description}</div>
                    )}
                    {s.published_school_id && (
                      <div className="text-[11px] text-[#166534] mt-2">
                        → sekolah: <code>{s.published_school_id}</code>
                      </div>
                    )}
                  </div>
                  {!isTerminal && editingSid !== sid && (
                    <div className="flex flex-wrap gap-2 shrink-0">
                      <Button onClick={() => openEdit(s)} data-testid={`suggestion-${sid}-edit`}
                              variant="outline"
                              className="rounded-full h-9 px-3 border-[#EAE7E0] text-[#4B5563] hover:bg-[#F3F1ED]">
                        Edit
                      </Button>
                      {status === "submitted" && (
                        <Button onClick={() => reviewSuggestion(sid)} data-testid={`suggestion-${sid}-review`}
                                className="rounded-full bg-[#1E3A8A] hover:bg-[#1E40AF] text-white h-9 px-4">
                          Setujui
                        </Button>
                      )}
                      {status === "reviewing" && (
                        <Button onClick={() => publishSuggestion(sid)} data-testid={`suggestion-${sid}-publish`}
                                className="rounded-full bg-[#166534] hover:bg-[#14532D] text-white h-9 px-4">
                          Publish
                        </Button>
                      )}
                      <Button onClick={() => markDuplicateSuggestion(sid)} data-testid={`suggestion-${sid}-duplicate`}
                              variant="outline"
                              className="rounded-full h-9 px-3 border-[#EAE7E0] text-[#3730A3] hover:bg-[#E0E7FF]">
                        Tandai Duplikat
                      </Button>
                      <Button onClick={() => rejectSuggestion(sid)} data-testid={`suggestion-${sid}-reject`}
                              variant="outline"
                              className="rounded-full h-9 px-3 border-[#EAE7E0] text-[#991B1B] hover:bg-[#FEE2E2]">
                        Tolak
                      </Button>
                    </div>
                  )}
                </div>

                {/* Inline edit form (iter153) */}
                {editingSid === sid && (
                  <div data-testid={`suggestion-${sid}-edit-form`} className="mt-4 pt-4 border-t border-[#EAE7E0] space-y-3">
                    <Input placeholder="Nama sekolah" value={editForm.name}
                           onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                           data-testid={`edit-${sid}-name`} className="rounded-xl border-[#EAE7E0]" />
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      <Input placeholder="Wilayah" value={editForm.city}
                             onChange={(e) => setEditForm({ ...editForm, city: e.target.value })}
                             data-testid={`edit-${sid}-city`} className="rounded-xl border-[#EAE7E0]" />
                      <Input placeholder="Provinsi" value={editForm.state}
                             onChange={(e) => setEditForm({ ...editForm, state: e.target.value })}
                             data-testid={`edit-${sid}-state`} className="rounded-xl border-[#EAE7E0]" />
                    </div>
                    <Select value={editForm.school_type}
                            onValueChange={(v) => setEditForm({ ...editForm, school_type: v })}>
                      <SelectTrigger data-testid={`edit-${sid}-type`} className="rounded-xl border-[#EAE7E0]">
                        <SelectValue placeholder="Jenis sekolah" />
                      </SelectTrigger>
                      <SelectContent>
                        {TYPE_OPTIONS.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <div>
                      <div className="text-[10px] uppercase tracking-widest text-[#4B5563] mb-1.5">Jenjang</div>
                      <div className="flex flex-wrap gap-2">
                        {GRADE_OPTIONS.map((g) => (
                          <button key={g.value} type="button"
                                  onClick={() => toggleEditGrade(g.value)}
                                  data-testid={`edit-${sid}-grade-${g.value}`}
                                  className={"px-3 py-1 rounded-full text-xs border transition-colors " +
                                    ((editForm.grade_levels || []).includes(g.value)
                                      ? "bg-[#1E3A8A] text-white border-[#1E3A8A]"
                                      : "bg-white text-[#4B5563] border-[#EAE7E0]")}>
                            {g.label}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-widest text-[#4B5563] mb-1.5">Kurikulum</div>
                      <div className="flex flex-wrap gap-2">
                        {CURRICULUM_OPTIONS.map((c) => (
                          <button key={c.value} type="button"
                                  onClick={() => toggleEditCurriculum(c.value)}
                                  data-testid={`edit-${sid}-curriculum-${c.value}`}
                                  className={"px-3 py-1 rounded-full text-xs border transition-colors " +
                                    ((editForm.curriculum || []).includes(c.value)
                                      ? "bg-[#1E3A8A] text-white border-[#1E3A8A]"
                                      : "bg-white text-[#4B5563] border-[#EAE7E0]")}>
                            {c.label}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      <Input placeholder="Nama grup/brand" value={editForm.brand_id}
                             onChange={(e) => setEditForm({ ...editForm, brand_id: e.target.value })}
                             data-testid={`edit-${sid}-brand`} className="rounded-xl border-[#EAE7E0]" />
                      <Input placeholder="Nama cabang" value={editForm.branch_name}
                             onChange={(e) => setEditForm({ ...editForm, branch_name: e.target.value })}
                             data-testid={`edit-branch-${i}`} className="h-9 rounded-md border-[#EAE7E0]" />
                      <Input placeholder="Alias/singkatan (mis: BSJ, SIS) — dipisahkan koma"
                             value={(editForm.aliases || []).join(", ")}
                             onChange={(e) => setEditForm({
                               ...editForm,
                               aliases: e.target.value.split(",").map((s) => s.trim()).filter(Boolean),
                             })}
                             data-testid={`edit-aliases-${i}`}
                             className="h-9 rounded-md border-[#EAE7E0] col-span-2" />
                             data-testid={`edit-${sid}-branch`} className="rounded-xl border-[#EAE7E0]" />
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      <Input type="number" min="0" placeholder="Estimasi uang pangkal"
                             value={editForm.entry_fee_amount}
                             onChange={(e) => setEditForm({ ...editForm, entry_fee_amount: e.target.value })}
                             data-testid={`edit-${sid}-entry-fee`} className="rounded-xl border-[#EAE7E0]" />
                      <Input type="number" min="0" placeholder="Estimasi SPP/bulan"
                             value={editForm.monthly_spp_amount}
                             onChange={(e) => setEditForm({ ...editForm, monthly_spp_amount: e.target.value })}
                             data-testid={`edit-${sid}-monthly-spp`} className="rounded-xl border-[#EAE7E0]" />
                    </div>
                    <Select value={editForm.accreditation_grade || "none"}
                            onValueChange={(v) => setEditForm({ ...editForm, accreditation_grade: v === "none" ? "" : v })}>
                      <SelectTrigger data-testid={`edit-${sid}-accreditation`} className="rounded-xl border-[#EAE7E0]">
                        <SelectValue placeholder="Akreditasi" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Belum diakreditasi</SelectItem>
                        <SelectItem value="A">A</SelectItem>
                        <SelectItem value="B">B</SelectItem>
                        <SelectItem value="C">C</SelectItem>
                      </SelectContent>
                    </Select>
                    <Textarea placeholder="Deskripsi" rows={3} value={editForm.description}
                              onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                              data-testid={`edit-${sid}-description`} className="rounded-xl border-[#EAE7E0]" />
                    <Input placeholder="Situs web" value={editForm.website}
                           onChange={(e) => setEditForm({ ...editForm, website: e.target.value })}
                           data-testid={`edit-${sid}-website`} className="rounded-xl border-[#EAE7E0]" />
                    <Input placeholder="URL gambar" value={editForm.image_url}
                           onChange={(e) => setEditForm({ ...editForm, image_url: e.target.value })}
                           data-testid={`edit-${sid}-image`} className="rounded-xl border-[#EAE7E0]" />
                    <div className="flex justify-end gap-2 pt-1">
                      <Button onClick={cancelEdit} data-testid={`edit-${sid}-cancel`}
                              variant="outline" className="rounded-full h-9 px-4 border-[#EAE7E0] text-[#4B5563]">
                        Batal
                      </Button>
                      <Button onClick={() => saveEdit(sid)} disabled={editSaving}
                              data-testid={`edit-${sid}-save`}
                              className="rounded-full h-9 px-5 bg-[#1E3A8A] hover:bg-[#1E40AF] text-white">
                        {editSaving ? "Menyimpan…" : "Simpan"}
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </TabsContent>

        {/* ===== KOMUNITAS (Forum moderation & category CRUD) ===== */}
        <ForumTab
          isModOrAbove={isModOrAbove}
          isAdminOrSuper={isAdminOrSuper}
          isSuper={isSuper}
          communityTab={communityTab}
          setCommunityTab={setCommunityTab}
          allThreads={allThreads}
          allReplies={allReplies}
          catLabel={catLabel}
          togglePin={togglePin}
          deleteThread={deleteThread}
          deleteReplyMod={deleteReplyMod}
          onThreadsReload={() => api.get("/admin/threads?limit=200").then((r) => setAllThreads(r.data)).catch(() => {})}
          initialFilterRuang={pendingThreadsFilterRuang}
          onFilterRuangConsumed={() => setPendingThreadsFilterRuang("")}
          setActiveTab={setActiveTab}
        />

        {isModOrAbove && (
          <TabsContent value="article-discussions" className="mt-6" data-testid="article-discussions-panel">
            <ArticleDiscussionsTab />
          </TabsContent>
        )}

        {/* ===== LEGACY MAPPING (super admin only) ===== */}
        {isSuper && (
          <TabsContent value="forum-console" className="mt-6" data-testid="forum-console-panel">
            <div className="mb-4 rounded-xl border border-[#F59E0B] bg-[#FFFBEB] p-3 text-xs text-[#78350F]" data-testid="legacy-mapping-hint">
              <strong>Legacy Mapping — Tool Migrasi Sementara.</strong><br />
              Struktur forum lama untuk proses migrasi ke Ruang. Tidak digunakan sebagai taxonomy Forum baru.
              Setelah 100% legacy threads sudah di-mapped ke Ruang, section ini akan dipensiunkan.
            </div>
            <ForumConsole />
          </TabsContent>
        )}

        {/* ===== FOTO KIRIMAN PENGGUNA (moderator+) ===== */}
        {isModOrAbove && (
          <TabsContent value="photos" className="mt-6" data-testid="photos-panel">
            <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
              <p className="text-sm text-[#4B5563] max-w-2xl">Semua foto kiriman pengguna & admin. Klik <em>Pin</em> untuk menempatkan foto di urutan teratas galeri sekolah publik. Hapus foto yang tidak pantas.</p>
              <div className="flex items-end gap-2">
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-[#4B5563] mb-1">Sumber</div>
                  <Select value={photoSourceFilter} onValueChange={setPhotoSourceFilter}>
                    <SelectTrigger data-testid="photos-source-filter" className="w-44 h-9"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Semua sumber</SelectItem>
                      <SelectItem value="parent_review">Dari review</SelectItem>
                      <SelectItem value="parent_direct">Kiriman orang tua langsung</SelectItem>
                      <SelectItem value="admin_direct">Diunggah admin</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-[#4B5563] mb-1">Status pin</div>
                  <Select value={photoPinFilter} onValueChange={setPhotoPinFilter}>
                    <SelectTrigger data-testid="photos-pin-filter" className="w-36 h-9"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Semua</SelectItem>
                      <SelectItem value="pinned">Sudah dipin</SelectItem>
                      <SelectItem value="unpinned">Belum dipin</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Button onClick={loadAllPhotos} data-testid="photos-apply-filter" className="rounded-full bg-[#1E3A8A] hover:bg-[#1E40AF] text-white h-9">Terapkan</Button>
              </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
              {allPhotos.map((p, i) => (
                <div key={p.photo_id} data-testid={`photo-tile-${i}`} className={"bg-white rounded-2xl overflow-hidden border shadow-sm " + (p.pinned ? "border-[#9A3412] ring-2 ring-[#FED7AA]" : "border-[#EAE7E0]")}>
                  <button onClick={() => setPreviewImg(p.data_url)} className="block w-full aspect-[4/3] bg-[#F3F1ED] relative">
                    <img src={p.data_url} alt={p.caption || "foto"} className="w-full h-full object-cover" />
                    {p.pinned && <span className="absolute top-2 left-2 px-2 py-0.5 rounded-full bg-[#FED7AA] text-[#9A3412] text-[10px] uppercase tracking-wider font-semibold inline-flex items-center gap-1"><Pin size={10} /> Dipin</span>}
                    <span className="absolute top-2 right-2 px-2 py-0.5 rounded-full bg-black/60 text-white text-[9px] uppercase tracking-wider">{p.source.replace("_", " ")}</span>
                  </button>
                  <div className="p-3">
                    <div className="text-xs text-[#1F2937] font-medium truncate">{p.school_name}</div>
                    <div className="text-[10px] text-[#4B5563] mt-0.5 truncate">oleh {p.uploader_name} · {new Date(p.created_at).toLocaleDateString("id-ID")}</div>
                    {p.caption && <div className="text-[11px] text-[#4B5563] mt-1 line-clamp-2 italic">"{p.caption}"</div>}
                    <div className="mt-2 flex items-center justify-end gap-1.5">
                      <Button variant="outline" onClick={() => togglePhotoPin(p)} data-testid={`photo-pin-${i}`} className={"rounded-full h-8 px-3 " + (p.pinned ? "border-[#9A3412] text-[#9A3412]" : "border-[#EAE7E0]")}>
                        <Pin size={12} className="mr-1" /> {p.pinned ? "Lepas" : "Pin"}
                      </Button>
                      <Button variant="outline" onClick={() => deletePhoto(p)} data-testid={`photo-delete-${i}`} className="rounded-full border-[#EAE7E0] text-[#B91C1C] h-8 px-3">
                        <Trash2 size={12} />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
              {allPhotos.length === 0 && <div className="col-span-full text-center text-[#4B5563] p-8" data-testid="photos-empty">Belum ada foto sesuai filter.</div>}
            </div>
            <div className="text-xs text-[#4B5563] mt-3">Total ditampilkan: {allPhotos.length} foto.</div>
          </TabsContent>
        )}

        {/* ===== PHOTO REPORTS ===== */}
        {isModOrAbove && (
          <TabsContent value="photo-reports" className="mt-6" data-testid="photo-reports-panel">
            <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
              <p className="text-sm text-[#4B5563] max-w-2xl">Foto yang dilaporkan pengguna. Tinjau, lalu <em>tutup</em> laporan bila tidak ada masalah atau hapus foto langsung dari sini.</p>
              <div className="flex items-end gap-2">
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-[#4B5563] mb-1">Status</div>
                  <Select value={photoReportStatus} onValueChange={setPhotoReportStatus}>
                    <SelectTrigger data-testid="photo-reports-status-filter" className="w-40 h-9"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="open">Terbuka</SelectItem>
                      <SelectItem value="dismissed">Ditutup</SelectItem>
                      <SelectItem value="actioned">Ditindaklanjuti</SelectItem>
                      <SelectItem value="all">Semua</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Button onClick={loadPhotoReports} data-testid="photo-reports-apply" className="rounded-full bg-[#1E3A8A] hover:bg-[#1E40AF] text-white h-9">Terapkan</Button>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {photoReports.map((r, i) => (
                <div key={r.report_id} data-testid={`photo-report-tile-${i}`} className="bg-white rounded-2xl overflow-hidden border border-[#EAE7E0] shadow-sm">
                  {r.photo_data_url ? (
                    <button onClick={() => setPreviewImg(r.photo_data_url)} className="block w-full aspect-[4/3] bg-[#F3F1ED]">
                      <img src={r.photo_data_url} alt="" className="w-full h-full object-cover" />
                    </button>
                  ) : (
                    <div className="w-full aspect-[4/3] bg-[#F3F1ED] flex items-center justify-center text-[#4B5563] text-xs">(foto sudah dihapus)</div>
                  )}
                  <div className="p-3 space-y-1.5">
                    <div className="flex items-center justify-between gap-2">
                      <div className="text-xs text-[#1F2937] font-medium truncate">{r.school_name}</div>
                      <span className={"px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider " + (r.status === "open" ? "bg-[#FEE2E2] text-[#B91C1C]" : r.status === "actioned" ? "bg-[#FEF3C7] text-[#92400E]" : "bg-[#E5E7EB] text-[#4B5563]")}>{r.status}</span>
                    </div>
                    <div className="text-[10px] text-[#4B5563]">Alasan: <span className="font-semibold text-[#1F2937]">{r.reason}</span></div>
                    {r.detail && <div className="text-[11px] text-[#4B5563] italic line-clamp-3">"{r.detail}"</div>}
                    <div className="text-[10px] text-[#4B5563]">Pelapor: {r.reporter_name} · {new Date(r.created_at).toLocaleDateString("id-ID")}</div>
                    {r.status === "open" && (
                      <div className="mt-2 flex items-center justify-end gap-1.5">
                        <Button variant="outline" onClick={() => resolvePhotoReport(r, "dismissed")} data-testid={`photo-report-dismiss-${i}`} className="rounded-full h-8 px-3 border-[#EAE7E0]">Tutup</Button>
                        {r.photo_data_url && (
                          <Button variant="outline" onClick={() => deleteReportedPhoto(r)} data-testid={`photo-report-delete-${i}`} className="rounded-full h-8 px-3 border-[#EAE7E0] text-[#B91C1C]">
                            <Trash2 size={12} className="mr-1" /> Hapus foto
                          </Button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ))}
              {photoReports.length === 0 && <div className="col-span-full text-center text-[#4B5563] p-8" data-testid="photo-reports-empty">Tidak ada laporan foto pada status ini.</div>}
            </div>
          </TabsContent>
        )}

        {/* ===== REVIEW REPORTS (post-moderation) ===== */}
        {isModOrAbove && (
          <TabsContent value="review-reports" className="mt-6" data-testid="review-reports-panel">
            <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
              <p className="text-sm text-[#4B5563] max-w-2xl">Review yang dilaporkan pengguna. Tinjau isi review, lalu <em>tutup</em> bila tidak ada masalah atau <em>hapus review</em> jika melanggar pedoman.</p>
              <div className="flex items-end gap-2">
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-[#4B5563] mb-1">Status</div>
                  <Select value={reviewReportStatus} onValueChange={setReviewReportStatus}>
                    <SelectTrigger data-testid="review-reports-status-filter" className="w-40 h-9"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="open">Terbuka</SelectItem>
                      <SelectItem value="dismissed">Ditutup</SelectItem>
                      <SelectItem value="actioned">Ditindaklanjuti</SelectItem>
                      <SelectItem value="all">Semua</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Button onClick={loadReviewReports} data-testid="review-reports-apply" className="rounded-full bg-[#1E3A8A] hover:bg-[#1E40AF] text-white h-9">Terapkan</Button>
              </div>
            </div>
            <div className="space-y-3">
              {reviewReports.map((r, i) => (
                <div key={r.report_id} data-testid={`review-report-tile-${i}`} className="bg-white border border-[#EAE7E0] rounded-2xl p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="text-xs uppercase tracking-widest text-[#4B5563] flex items-center gap-2 flex-wrap">
                        {r.school_name}
                        <span className={"px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider " + (r.status === "open" ? "bg-[#FEE2E2] text-[#B91C1C]" : r.status === "actioned" ? "bg-[#FEF3C7] text-[#92400E]" : "bg-[#E5E7EB] text-[#4B5563]")}>{r.status}</span>
                        <span>· {new Date(r.created_at).toLocaleDateString("id-ID")}</span>
                        <span>· Alasan: <strong className="normal-case tracking-normal text-[#1F2937]">{r.reason}</strong></span>
                      </div>
                      <div className="font-serif-display text-base text-[#1F2937] mt-1">{r.review_title || "(review sudah dihapus)"}</div>
                      {r.review_body && <p className="mt-1 text-sm text-[#4B5563] line-clamp-3">{r.review_body}</p>}
                      <div className="mt-1.5 text-[11px] text-[#4B5563]">Review oleh: <strong className="text-[#1F2937]">{r.review_author || "—"}</strong> · Dilaporkan oleh {r.reporter_name}</div>
                      {r.detail && <div className="mt-1 text-[11px] text-[#4B5563] italic">"{r.detail}"</div>}
                    </div>
                    {r.status === "open" && (
                      <div className="flex flex-col gap-1.5 shrink-0">
                        <Button variant="outline" onClick={() => resolveReviewReport(r, "dismissed")} data-testid={`review-report-dismiss-${i}`} className="rounded-full h-9 px-3 border-[#EAE7E0]">Tutup</Button>
                        {r.review_title && (
                          <Button variant="outline" onClick={() => deleteReportedReview(r)} data-testid={`review-report-delete-${i}`} className="rounded-full h-9 px-3 border-[#EAE7E0] text-[#B91C1C]">
                            <Trash2 size={12} className="mr-1" /> Hapus review
                          </Button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ))}
              {reviewReports.length === 0 && <div className="text-center text-[#4B5563] p-8" data-testid="review-reports-empty">Tidak ada laporan review pada status ini.</div>}
            </div>
          </TabsContent>
        )}

        {/* ===== FORUM REPORTS (thread + reply moderation queue) ===== */}
        {isModOrAbove && (
          <TabsContent value="forum-reports" className="mt-6" data-testid="forum-reports-panel">
            <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
              <p className="text-sm text-[#4B5563] max-w-2xl">
                Laporan pengguna atas thread & balasan. Klik <em>Sembunyikan</em> untuk moderasi ringan (bisa dipulihkan) atau <em>Hapus Permanen</em> untuk pelanggaran berat. Postingan tetap terlihat di alur dengan pesan moderasi.
              </p>
              <div className="flex items-end gap-2">
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-[#4B5563] mb-1">Status</div>
                  <Select value={forumReportStatus} onValueChange={setForumReportStatus}>
                    <SelectTrigger data-testid="forum-reports-status-filter" className="w-40 h-9"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="open">Terbuka</SelectItem>
                      <SelectItem value="dismissed">Ditutup</SelectItem>
                      <SelectItem value="actioned">Ditindaklanjuti</SelectItem>
                      <SelectItem value="all">Semua</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Button onClick={loadForumReports} data-testid="forum-reports-apply" className="rounded-full bg-[#1E3A8A] hover:bg-[#1E40AF] text-white h-9">Terapkan</Button>
              </div>
            </div>
            <div className="space-y-3">
              {forumReports.map((r, i) => {
                const reasonLabel = ({
                  spam: "Spam",
                  sara: "Mengandung SARA",
                  bahasa: "Bahasa Tidak Sesuai",
                  misinformasi: "Salah Informasi",
                  other: "Lainnya",
                }[r.reason] || r.reason);
                return (
                  <div key={r.report_id} data-testid={`forum-report-tile-${i}`} className="bg-white border border-[#EAE7E0] rounded-2xl p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="text-xs uppercase tracking-widest text-[#4B5563] flex items-center gap-2 flex-wrap">
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#EFF6FF] text-[#1E3A8A] text-[10px]">
                            {r.target_type === "thread" ? "Thread" : "Balasan"}
                          </span>
                          <span className={"px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider " + (r.status === "open" ? "bg-[#FEE2E2] text-[#B91C1C]" : r.status === "actioned" ? "bg-[#FEF3C7] text-[#92400E]" : "bg-[#E5E7EB] text-[#4B5563]")}>{r.status}</span>
                          {r.is_deleted && <span className="px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider bg-[#B91C1C] text-white">dihapus permanen</span>}
                          {r.is_hidden && !r.is_deleted && <span className="px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider bg-[#FDE68A] text-[#78350F]">disembunyikan</span>}
                          <span>· {new Date(r.created_at).toLocaleDateString("id-ID")}</span>
                          <span>· Alasan: <strong className="normal-case tracking-normal text-[#1F2937]">{reasonLabel}</strong></span>
                        </div>
                        {r.content_title && <div className="font-serif-display text-base text-[#1F2937] mt-1">{r.content_title}</div>}
                        {r.content_body && (
                          <p className="mt-1 text-sm text-[#4B5563] whitespace-pre-wrap line-clamp-4" data-testid={`forum-report-body-${i}`}>{r.content_body}</p>
                        )}
                        {!r.content_body && !r.content_title && <div className="text-sm text-[#4B5563] italic">(konten sudah dihapus)</div>}
                        <div className="mt-1.5 text-[11px] text-[#4B5563]">
                          Konten oleh: <strong className="text-[#1F2937]">{r.content_author || r.target_author_name || "—"}</strong> · Dilaporkan oleh <strong className="text-[#1F2937]">{r.reporter_name}</strong>
                          {r.thread_id && (
                            <>
                              {" · "}
                              <a href={`/forum/${r.thread_id}${r.target_type === "reply" ? `#reply-${r.target_id}` : ""}`} target="_blank" rel="noreferrer" className="text-[#1E3A8A] hover:underline" data-testid={`forum-report-open-${i}`}>
                                Buka di forum →
                              </a>
                            </>
                          )}
                        </div>
                        {r.detail && <div className="mt-1 text-[11px] text-[#4B5563] italic">"{r.detail}"</div>}
                      </div>
                      <div className="flex flex-col gap-1.5 shrink-0">
                        {r.status === "open" && (
                          <Button variant="outline" onClick={() => resolveForumReport(r, "dismissed")} data-testid={`forum-report-dismiss-${i}`} className="rounded-full h-9 px-3 border-[#EAE7E0]">
                            Tutup laporan
                          </Button>
                        )}
                        {!r.is_deleted && !r.is_hidden && (
                          <>
                            <Button variant="outline" onClick={() => moderateForumTarget(r, "hide")} data-testid={`forum-report-hide-${i}`} className="rounded-full h-9 px-3 border-[#FDE68A] text-[#78350F]">
                              Sembunyikan
                            </Button>
                            <Button variant="outline" onClick={() => moderateForumTarget(r, "delete")} data-testid={`forum-report-delete-${i}`} className="rounded-full h-9 px-3 border-[#B91C1C] text-[#B91C1C]">
                              <Trash2 size={12} className="mr-1" /> Hapus Permanen
                            </Button>
                          </>
                        )}
                        {r.is_hidden && !r.is_deleted && (
                          <Button variant="outline" onClick={() => unhideForumTarget(r)} data-testid={`forum-report-unhide-${i}`} className="rounded-full h-9 px-3 border-[#166534] text-[#166534]">
                            Pulihkan
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
              {forumReports.length === 0 && <div className="text-center text-[#4B5563] p-8" data-testid="forum-reports-empty">Tidak ada laporan forum pada status ini.</div>}
            </div>
          </TabsContent>
        )}

        {/* ===== STATIC PAGES CMS ===== */}
        {isAdminOrSuper && (
          <TabsContent value="pages" className="mt-6 space-y-3" data-testid="pages-panel">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <p className="text-sm text-[#4B5563]">Kelola halaman statis (Tentang Kami, Kebijakan Privasi, FAQ, dll). Semua halaman yang dipublikasikan otomatis tampil di footer situs.</p>
              <Button onClick={openNewPage} data-testid="new-page-btn" className="rounded-full bg-[#E07A5F] hover:bg-[#C96D55] text-white">
                <Plus size={14} className="mr-1" /> Halaman baru
              </Button>
            </div>
            <div className="bg-white border border-[#EAE7E0] rounded-2xl overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-[#F3F1ED] text-xs uppercase tracking-widest text-[#4B5563]">
                  <tr>
                    <th className="text-left p-3">Judul</th>
                    <th className="text-left p-3 w-40">Slug</th>
                    <th className="text-left p-3 w-24">Status</th>
                    <th className="text-left p-3 w-32">Diperbarui</th>
                    <th className="text-right p-3 w-40">Aksi</th>
                  </tr>
                </thead>
                <tbody>
                  {staticPages.map((p, i) => (
                    <tr key={p.slug} data-testid={`page-row-${i}`} className="border-t border-[#EAE7E0]">
                      <td className="p-3">
                        <a href={`/halaman/${p.slug}`} target="_blank" rel="noreferrer" className="text-[#1F2937] font-medium hover:text-[#E07A5F]">{p.title}</a>
                      </td>
                      <td className="p-3 text-xs text-[#4B5563]"><code>{p.slug}</code></td>
                      <td className="p-3">
                        {p.published ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#DCFCE7] text-[#166534] text-[10px] uppercase tracking-wider border border-[#BBF7D0]">Publik</span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#F3F1ED] text-[#4B5563] text-[10px] uppercase tracking-wider">Draft</span>
                        )}
                      </td>
                      <td className="p-3 text-xs text-[#4B5563]">{new Date(p.updated_at || p.created_at).toLocaleDateString("id-ID")}</td>
                      <td className="p-3 text-right">
                        <Button variant="outline" onClick={() => openEditPage(p)} data-testid={`edit-page-${i}`} className="rounded-full h-8 mr-2 border-[#EAE7E0]"><Pencil size={12} className="mr-1" /> Edit</Button>
                        <Button variant="outline" onClick={() => deletePage2(p)} data-testid={`delete-page-${i}`} className="rounded-full h-8 border-[#EAE7E0] text-[#B91C1C]"><Trash2 size={12} /></Button>
                      </td>
                    </tr>
                  ))}
                  {staticPages.length === 0 && <tr><td colSpan={5} className="p-6 text-center text-[#4B5563]">Belum ada halaman. Klik "Halaman baru" untuk menambah.</td></tr>}
                </tbody>
              </table>
            </div>
          </TabsContent>
        )}


        {/* ===== STATISTIK ===== */}
        <TabsContent value="stats" className="mt-6" data-testid="stats-panel">
          {!stats && <div className="text-[#4B5563]">Memuat…</div>}
          {stats && (
            <>
              <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
                <p className="text-sm text-[#4B5563] max-w-xl">Rasio review wali murid terverifikasi per sekolah. Rasio ≥ 50% dan ≥ 2 review terverifikasi = lencana "Trusted".</p>
                <button onClick={downloadCsv} data-testid="stats-download-csv" className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#1E3A8A] hover:bg-[#1E40AF] text-white text-sm">
                  <Download size={14} /> Unduh CSV
                </button>
              </div>
              <div className="grid sm:grid-cols-3 gap-4 mb-8">
                <StatCard label="Total review" value={stats.total_reviews} />
                <StatCard label="Review terverifikasi" value={stats.total_verified} accent="#166534" />
                <StatCard label="Rasio keseluruhan" value={`${stats.overall_ratio}%`} accent="#1E3A8A" />
              </div>
              <div className="bg-white border border-[#EAE7E0] rounded-2xl overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-[#F3F1ED] text-xs uppercase tracking-widest text-[#4B5563]">
                    <tr><th className="text-left p-4">Sekolah</th><th className="text-left p-4">Wilayah</th><th className="text-right p-4">Total</th><th className="text-right p-4">Terverifikasi</th><th className="text-left p-4 w-56">Rasio</th></tr>
                  </thead>
                  <tbody>
                    {stats.per_school.map((row) => (
                      <tr key={row.school_id} className="border-t border-[#EAE7E0]">
                        <td className="p-4 text-[#1F2937]">{row.school_name}</td>
                        <td className="p-4 text-[#4B5563]">{row.city}</td>
                        <td className="p-4 text-right">{row.total_reviews}</td>
                        <td className="p-4 text-right font-medium text-[#166534]">{row.verified_reviews}</td>
                        <td className="p-4">
                          <div className="flex items-center gap-3">
                            <div className="flex-1 h-2 rounded-full bg-[#F3F1ED] overflow-hidden">
                              <div className="h-full bg-[#166534]" style={{ width: `${row.verified_ratio}%` }} />
                            </div>
                            <span className="text-xs text-[#4B5563] w-12 text-right">{row.verified_ratio}%</span>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </TabsContent>

        {/* ===== USER MANAGEMENT (SUPER ONLY) ===== */}
        {isSuper && (
          <TabsContent value="users" className="mt-6" data-testid="users-panel">
            <div className="flex justify-between items-center mb-4">
              <p className="text-sm text-[#4B5563]">Semua akun admin. Hanya Super Admin yang bisa menambah, mengubah peran, atau menghapus akun.</p>
              <Dialog open={newAdminOpen} onOpenChange={setNewAdminOpen}>
                <DialogTrigger asChild>
                  <Button data-testid="new-admin-btn" className="rounded-full bg-[#1E3A8A] hover:bg-[#1E40AF] text-white"><Plus size={14} className="mr-1" /> Tambah admin</Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader><DialogTitle>Buat akun admin</DialogTitle></DialogHeader>
                  <form onSubmit={createAdmin} className="space-y-3 mt-2">
                    <Field label="Nama lengkap" required>
                      <Input placeholder="Nama lengkap admin" value={newAdmin.name} onChange={(e) => setNewAdmin({ ...newAdmin, name: e.target.value })} data-testid="admin-name" required />
                    </Field>
                    <Field label="Email" required>
                      <Input type="email" placeholder="admin@sekolah.id" value={newAdmin.email} onChange={(e) => setNewAdmin({ ...newAdmin, email: e.target.value })} data-testid="admin-email" required />
                    </Field>
                    <Field label="Password" required hint="Minimal 6 karakter">
                      <Input type="password" placeholder="Minimal 6 karakter" value={newAdmin.password} onChange={(e) => setNewAdmin({ ...newAdmin, password: e.target.value })} data-testid="admin-password" required minLength={6} />
                    </Field>
                    <Field label="Peran (role)" required>
                    <Select value={newAdmin.role} onValueChange={(v) => setNewAdmin({ ...newAdmin, role: v })}>
                      <SelectTrigger data-testid="admin-role"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="admin">Admin (kelola sekolah + moderasi review)</SelectItem>
                        <SelectItem value="moderator">Moderator (hanya moderasi review)</SelectItem>
                        <SelectItem value="superadmin">Super Admin (hak penuh)</SelectItem>
                      </SelectContent>
                    </Select>
                    </Field>
                    <Button type="submit" className="w-full rounded-full bg-[#E07A5F] hover:bg-[#C96D55] text-white">Buat akun</Button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
            <div className="bg-white border border-[#EAE7E0] rounded-2xl overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-[#F3F1ED] text-xs uppercase tracking-widest text-[#4B5563]">
                  <tr><th className="text-left p-4">Nama</th><th className="text-left p-4">Email</th><th className="text-left p-4">Peran</th><th className="text-right p-4">Aksi</th></tr>
                </thead>
                <tbody>
                  {admins.map((a, i) => (
                    <tr key={a.user_id} data-testid={`admin-row-${i}`} className="border-t border-[#EAE7E0]">
                      <td className="p-4 text-[#1F2937]">{a.name}</td>
                      <td className="p-4 text-[#4B5563]">{a.email}</td>
                      <td className="p-4">
                        <Select value={a.role} onValueChange={(v) => changeRole(a.user_id, v)} disabled={a.user_id === user.user_id}>
                          <SelectTrigger data-testid={`admin-role-${i}`} className="w-40 h-8"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="admin">Admin</SelectItem>
                            <SelectItem value="moderator">Moderator</SelectItem>
                            <SelectItem value="superadmin">Super Admin</SelectItem>
                            <SelectItem value="parent">Parent (demote)</SelectItem>
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="p-4 text-right">
                        {a.user_id !== user.user_id && (
                          <Button variant="outline" onClick={() => deleteAdmin(a.user_id)} data-testid={`admin-delete-${i}`} className="rounded-full border-[#EAE7E0] text-[#B91C1C]">
                            <Trash2 size={14} />
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>
        )}

        {/* ===== PENGGUNA (all users + Ban/Unban) — SUPER ADMIN ONLY ===== */}
        {isSuper && (
          <UsersTab
            user={user}
            sortedAllUsers={sortedAllUsers}
            usersQuery={usersQuery}
            setUsersQuery={setUsersQuery}
            usersRoleFilter={usersRoleFilter}
            setUsersRoleFilter={setUsersRoleFilter}
            usersBannedFilter={usersBannedFilter}
            setUsersBannedFilter={setUsersBannedFilter}
            usersSortBy={usersSortBy}
            setUsersSortBy={setUsersSortBy}
            loadAllUsers={loadAllUsers}
            setDetailUserId={setDetailUserId}
            openBanDialog={openBanDialog}
            unbanUser={unbanUser}
            toggleFoundingParent={toggleFoundingParent}
          />
        )}

        {/* User Detail Drawer (super admin) */}
        {isSuper && (
          <UserDetailDrawer
            userId={detailUserId}
            open={!!detailUserId}
            onOpenChange={(v) => { if (!v) setDetailUserId(null); }}
            currentUserId={user.user_id}
            onChanged={() => { loadAllUsers(); loadAdminActions && loadAdminActions(); }}
          />
        )}

        {/* ===== LEVEL & LEADERBOARD (Super Admin) ===== */}
        {isSuper && (
          <TabsContent value="gamification" className="mt-6 space-y-8" data-testid="gamification-panel">
            {/* Master toggle — hide badges app-wide without wiping recorded contributions */}
            {levelConfigDraft && (
              <div className="rounded-2xl border border-[#EAE7E0] bg-white p-4 sm:p-5 flex items-start gap-4" data-testid="gamification-master-toggle">
                <div className="flex-1 min-w-0">
                  <div className="text-xs uppercase tracking-widest text-[#4B5563] mb-1">Sistem Gamifikasi</div>
                  <div className="font-serif-display text-lg text-[#1F2937] leading-snug">
                    {levelConfigDraft.enabled === false ? "Level & lencana disembunyikan" : "Level & lencana ditampilkan"}
                  </div>
                  <p className="text-[13px] text-[#4B5563] mt-1 leading-relaxed max-w-xl">
                    Nonaktifkan untuk menyembunyikan seluruh lencana level (Wali Baru, Pakar Sekolah, dsb) di kartu diskusi, review, dan halaman profil. Kontribusi pengguna tetap dihitung di latar belakang — Anda bisa mengaktifkannya lagi kapan saja tanpa kehilangan data.
                  </p>
                </div>
                <label className="inline-flex items-center gap-3 shrink-0 mt-1">
                  <Switch
                    checked={levelConfigDraft.enabled !== false}
                    onCheckedChange={toggleGamification}
                    data-testid="gamification-toggle-switch"
                  />
                  <span className={"text-xs font-medium " + (levelConfigDraft.enabled === false ? "text-[#8A8F99]" : "text-[#166534]")}>
                    {levelConfigDraft.enabled === false ? "Nonaktif" : "Aktif"}
                  </span>
                </label>
              </div>
            )}

            {/* Level configuration editor */}
            <div>
              <div className="flex items-end justify-between flex-wrap gap-3 mb-4">
                <div>
                  <h3 className="font-serif-display text-2xl text-[#1F2937]">Konfigurasi Level Pengguna</h3>
                  <p className="text-sm text-[#4B5563] mt-1">Ubah nama level dan jumlah poin per aktivitas. Perubahan disimpan akan langsung diterapkan ke semua pengguna.</p>
                </div>
                <Button onClick={saveLevelConfig} data-testid="save-level-config" className="rounded-full bg-[#1E3A8A] hover:bg-[#1E40AF] text-white">Simpan konfigurasi</Button>
              </div>

              {levelConfigDraft && (
                <div className="grid lg:grid-cols-2 gap-4">
                  {/* Levels editor */}
                  <div className="bg-white border border-[#EAE7E0] rounded-2xl p-5">
                    <div className="text-xs uppercase tracking-widest text-[#4B5563] mb-3">Tingkatan level (nama & minimum poin)</div>
                    <div className="space-y-2">
                      {levelConfigDraft.levels.map((lv, i) => (
                        <div key={i} className="flex items-center gap-2" data-testid={`level-row-${i}`}>
                          <span className="w-6 text-center text-xs text-[#4B5563] font-semibold">{i + 1}.</span>
                          <Input value={lv.name} onChange={(e) => updateDraftLevel(i, "name", e.target.value)} data-testid={`level-name-${i}`} className="flex-1" placeholder="Nama level" />
                          <Input type="number" value={lv.min_points} onChange={(e) => updateDraftLevel(i, "min_points", Number(e.target.value) || 0)} data-testid={`level-minpoints-${i}`} className="w-24" placeholder="Min pts" />
                          <UserLevelBadge name={lv.name} order={i + 1} />
                        </div>
                      ))}
                    </div>
                  </div>
                  {/* Points editor */}
                  <div className="bg-white border border-[#EAE7E0] rounded-2xl p-5">
                    <div className="text-xs uppercase tracking-widest text-[#4B5563] mb-3">Poin per aktivitas (aktivitas yang di-approve)</div>
                    <div className="space-y-3">
                      {[
                        { key: "review", label: "Review disetujui" },
                        { key: "photo",  label: "Foto diunggah" },
                        { key: "thread", label: "Thread forum dibuat" },
                        { key: "reply",  label: "Balasan forum" },
                      ].map(({ key, label }) => (
                        <div key={key} className="flex items-center justify-between gap-3 p-3 rounded-xl bg-[#FAFAF8] border border-[#EAE7E0]">
                          <span className="text-sm text-[#1F2937]">{label}</span>
                          <div className="inline-flex items-center gap-2">
                            <Input type="number" value={levelConfigDraft.points[key] ?? 0} onChange={(e) => updateDraftPoints(key, e.target.value)} data-testid={`points-${key}`} className="w-20" />
                            <span className="text-xs text-[#4B5563]">poin</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Leaderboard */}
            <div>
              <div className="flex items-end justify-between flex-wrap gap-3 mb-4">
                <div>
                  <h3 className="font-serif-display text-2xl text-[#1F2937] inline-flex items-center gap-2"><Award className="text-[#78350F]" size={22} /> Papan Peringkat</h3>
                  <p className="text-sm text-[#4B5563] mt-1">50 kontributor teratas berdasarkan total poin aktivitas.</p>
                </div>
                <Button variant="outline" onClick={loadLeaderboard} data-testid="refresh-leaderboard" className="rounded-full border-[#EAE7E0]">Muat ulang</Button>
              </div>
              <div className="bg-white border border-[#EAE7E0] rounded-2xl overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-[#F3F1ED] text-xs uppercase tracking-widest text-[#4B5563]">
                    <tr>
                      <th className="text-left p-3 w-14">Rank</th>
                      <th className="text-left p-3">Pengguna</th>
                      <th className="text-left p-3 w-44">Level</th>
                      <th className="text-right p-3 w-24">Poin</th>
                      <th className="text-right p-3 w-56">Kontribusi</th>
                    </tr>
                  </thead>
                  <tbody>
                    {leaderboard.map((u, i) => {
                      const rankStyle = i === 0 ? "text-[#78350F]" : i === 1 ? "text-[#4B5563]" : i === 2 ? "text-[#9A3412]" : "text-[#4B5563]";
                      return (
                        <tr key={u.user_id} data-testid={`leaderboard-row-${i}`} className="border-t border-[#EAE7E0]">
                          <td className="p-3">
                            <span className={"font-serif-display text-lg " + rankStyle}>#{i + 1}</span>
                          </td>
                          <td className="p-3">
                            <div className="text-[#1F2937] font-medium">{u.name}</div>
                            <div className="text-xs text-[#4B5563]">{u.email} · {u.role}</div>
                          </td>
                          <td className="p-3"><UserLevelBadge name={u.level_name} order={u.level_order} /></td>
                          <td className="p-3 text-right font-serif-display text-lg text-[#1E3A8A]">{u.points || 0}</td>
                          <td className="p-3 text-right text-[11px] text-[#4B5563]">
                            {u.contribution_stats && (
                              <>
                                {u.contribution_stats.reviews || 0} review · {u.contribution_stats.photos || 0} foto
                                <br />
                                {u.contribution_stats.threads || 0} thread · {u.contribution_stats.replies || 0} balasan
                              </>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                    {leaderboard.length === 0 && <tr><td colSpan={5} className="p-6 text-center text-[#4B5563]">Belum ada pengguna aktif.</td></tr>}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>
        )}

        {/* ===== APLIKASI MODERATOR — SUPER ADMIN ONLY ===== */}
        {isSuper && (
          <TabsContent value="mod-apps" className="mt-6" data-testid="mod-apps-panel">
            <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
              <p className="text-sm text-[#4B5563] max-w-2xl">Aplikasi orang tua sukarelawan yang ingin menjadi moderator. Setujui untuk mempromosikan akun bersangkutan menjadi peran <em>moderator</em> otomatis (bila email sudah terdaftar).</p>
              <div className="flex items-end gap-2">
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-[#4B5563] mb-1">Status</div>
                  <Select value={modAppsStatus} onValueChange={setModAppsStatus}>
                    <SelectTrigger data-testid="mod-apps-status-filter" className="w-40 h-9"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pending">Menunggu</SelectItem>
                      <SelectItem value="approved">Disetujui</SelectItem>
                      <SelectItem value="rejected">Ditolak</SelectItem>
                      <SelectItem value="all">Semua</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Button onClick={loadModApps} data-testid="mod-apps-apply" className="rounded-full bg-[#1E3A8A] hover:bg-[#1E40AF] text-white h-9">Terapkan</Button>
              </div>
            </div>
            <div className="space-y-3">
              {modApps.map((a, i) => (
                <div key={a.application_id} data-testid={`mod-app-row-${i}`} className="bg-white border border-[#EAE7E0] rounded-2xl p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="text-xs text-[#4B5563] flex items-center gap-2">
                        <span className={"px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider " + (a.status === "pending" ? "bg-[#FEF3C7] text-[#92400E]" : a.status === "approved" ? "bg-[#DCFCE7] text-[#166534]" : "bg-[#E5E7EB] text-[#4B5563]")}>{a.status}</span>
                        · {new Date(a.created_at).toLocaleDateString("id-ID")} · {a.city || "—"} · {a.availability || "—"}
                      </div>
                      <div className="text-lg font-serif text-[#1F2937] mt-1">{a.name}</div>
                      <div className="text-xs text-[#4B5563]">{a.email}{a.phone ? ` · ${a.phone}` : ""}</div>
                      <div className="mt-2 text-sm text-[#1F2937]"><strong>Motivasi:</strong> {a.motivation}</div>
                      <div className="mt-1.5 text-sm text-[#1F2937]"><strong>Pengalaman:</strong> {a.experience}</div>
                      {a.ref_links && <div className="mt-1.5 text-xs text-[#1E3A8A]">Ref: {a.ref_links}</div>}
                      {a.reviewer_note && a.status !== "pending" && <div className="mt-1.5 text-[11px] text-[#4B5563] italic">Catatan reviewer: "{a.reviewer_note}"</div>}
                    </div>
                    {a.status === "pending" && (
                      <div className="flex flex-col gap-1.5 shrink-0">
                        <Button onClick={() => { setReviewingApp({ ...a, __action: "approve" }); setReviewNote(""); }} data-testid={`mod-app-approve-${i}`} className="rounded-full bg-[#1E3A8A] hover:bg-[#1E40AF] text-white h-9 px-4">Setujui</Button>
                        <Button variant="outline" onClick={() => { setReviewingApp({ ...a, __action: "reject" }); setReviewNote(""); }} data-testid={`mod-app-reject-${i}`} className="rounded-full h-9 px-4 border-[#EAE7E0] text-[#B91C1C]">Tolak</Button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
              {modApps.length === 0 && <div className="text-center text-[#4B5563] p-8" data-testid="mod-apps-empty">Tidak ada aplikasi pada status ini.</div>}
            </div>
          </TabsContent>
        )}

        {/* ===== LOG AKSI (audit trail) — SUPER ADMIN ONLY ===== */}
        {isSuper && (
          <TabsContent value="audit-log" className="mt-6" data-testid="audit-log-panel">
            <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
              <p className="text-sm text-[#4B5563] max-w-2xl">Riwayat lengkap aksi moderasi & administrasi: ban/unban akun, hapus thread/balasan/review/kategori/sekolah, pin thread, ubah tier premium. Digunakan untuk audit dan akuntabilitas.</p>
              <div className="flex items-end gap-2">
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-[#4B5563] mb-1">Filter aksi</div>
                  <Select value={actionFilter} onValueChange={(v) => { setActionFilter(v); }}>
                    <SelectTrigger data-testid="action-filter" className="w-48 h-9"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Semua aksi</SelectItem>
                      <SelectItem value="user_ban">Ban pengguna</SelectItem>
                      <SelectItem value="user_unban">Aktifkan pengguna</SelectItem>
                      <SelectItem value="user_unban_auto">Auto-unban (expiry)</SelectItem>
                      <SelectItem value="thread_delete">Hapus thread</SelectItem>
                      <SelectItem value="thread_pin">Pin thread</SelectItem>
                      <SelectItem value="thread_unpin">Lepas pin</SelectItem>
                      <SelectItem value="reply_delete">Hapus balasan</SelectItem>
                      <SelectItem value="review_delete">Hapus review</SelectItem>
                      <SelectItem value="category_delete">Hapus kategori</SelectItem>
                      <SelectItem value="school_delete">Hapus sekolah</SelectItem>
                      <SelectItem value="school_tier_change">Ubah tier sekolah</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Button onClick={loadAdminActions} data-testid="action-filter-apply" className="rounded-full bg-[#1E3A8A] hover:bg-[#1E40AF] text-white h-9">Terapkan</Button>
              </div>
            </div>
            <div className="bg-white border border-[#EAE7E0] rounded-2xl overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-[#F3F1ED] text-xs uppercase tracking-widest text-[#4B5563]">
                  <tr>
                    <th className="text-left p-3 w-48">Waktu</th>
                    <th className="text-left p-3 w-40">Aktor</th>
                    <th className="text-left p-3 w-44">Aksi</th>
                    <th className="text-left p-3">Target</th>
                    <th className="text-left p-3">Detail</th>
                  </tr>
                </thead>
                <tbody>
                  {adminActions.map((a, i) => (
                    <tr key={a.action_id} data-testid={`action-row-${i}`} className="border-t border-[#EAE7E0]">
                      <td className="p-3 text-xs text-[#4B5563] whitespace-nowrap">{new Date(a.created_at).toLocaleString("id-ID", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}</td>
                      <td className="p-3 text-[#1F2937]">
                        <div className="text-sm font-medium">{a.actor_name}</div>
                        <div className="text-[10px] text-[#4B5563]">{a.actor_role}</div>
                      </td>
                      <td className="p-3"><span className={"inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider " + actionBadgeCls(a.action)}>{a.action.replace(/_/g, " ")}</span></td>
                      <td className="p-3 text-[#4B5563]">
                        <div className="text-xs">{a.target_type}</div>
                        <div className="text-sm text-[#1F2937] line-clamp-1">{a.target_label || a.target_id}</div>
                      </td>
                      <td className="p-3 text-xs text-[#4B5563]">
                        {a.details && Object.keys(a.details).length > 0 && (
                          <pre className="whitespace-pre-wrap font-mono text-[10px] max-w-md truncate">{JSON.stringify(a.details, null, 0)}</pre>
                        )}
                      </td>
                    </tr>
                  ))}
                  {adminActions.length === 0 && <tr><td colSpan={5} className="p-6 text-center text-[#4B5563]">Belum ada aksi tercatat.</td></tr>}
                </tbody>
              </table>
            </div>
            <div className="text-xs text-[#4B5563] mt-3">Total ditampilkan: {adminActions.length} aksi.</div>
          </TabsContent>
        )}
      </Tabs>

      {/* Category composer dialog */}
      <Dialog
        open={newCatOpen || !!editingCat}
        onOpenChange={(v) => { if (!v) { setNewCatOpen(false); setEditingCat(null); setCatForm(emptyCat); } }}
      >
        <DialogContent>
          <DialogHeader><DialogTitle>{editingCat ? "Edit kategori forum" : "Buat kategori forum baru"}</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-2">
            <Field label="Judul kategori" required>
              <Input placeholder="mis: Penerimaan siswa baru" value={catForm.label} onChange={(e) => setCatForm({ ...catForm, label: e.target.value })} data-testid="cat-label" />
            </Field>
            <Field label="URL slug" required hint={editingCat ? "Slug tidak bisa diubah setelah kategori dibuat." : "Huruf kecil, tanpa spasi. Contoh: penerimaan-siswa-baru"}>
              <Input placeholder="mis: penerimaan-siswa-baru" value={catForm.slug} onChange={(e) => setCatForm({ ...catForm, slug: e.target.value })} data-testid="cat-slug" disabled={!!editingCat} />
            </Field>
            <Field label="Deskripsi singkat">
              <Textarea rows={2} placeholder="Ringkasan singkat kategori — muncul di halaman forum" value={catForm.description} onChange={(e) => setCatForm({ ...catForm, description: e.target.value })} data-testid="cat-description" />
            </Field>
            <Field label="Urutan tampil" hint="Angka lebih kecil ditampilkan lebih dulu">
              <Input type="number" placeholder="mis: 1" value={catForm.order} onChange={(e) => setCatForm({ ...catForm, order: e.target.value })} data-testid="cat-order" />
            </Field>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => { setNewCatOpen(false); setEditingCat(null); setCatForm(emptyCat); }} className="rounded-full">Batal</Button>
              <Button onClick={() => saveCategory(editingCat ? "edit" : "create")} data-testid="cat-save" className="rounded-full bg-[#1E3A8A] hover:bg-[#1E40AF] text-white">
                {editingCat ? "Simpan perubahan" : "Buat kategori"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Blog composer dialog (create or edit) */}
      <Dialog
        open={newPostOpen || !!editingPost}
        onOpenChange={(v) => { if (!v) { setNewPostOpen(false); setEditingPost(null); setPostForm(emptyPost); } }}
      >
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              <span>{editingPost ? "Edit artikel" : "Tulis artikel"}</span>
              <span data-testid="draft-status" className="text-[10px] font-normal uppercase tracking-widest text-[#4B5563]">
                {draftSaving ? "Menyimpan draft…" : draftSavedAt ? `Tersimpan ${new Date(draftSavedAt).toLocaleTimeString("id-ID", { hour: "2-digit", minute: "2-digit" })}` : "Auto-save aktif · setiap 30 detik"}
              </span>
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 mt-2 max-h-[70vh] overflow-y-auto pr-1">
            <Field label="Judul artikel" required>
              <Input placeholder="mis: 5 Sekolah Terbaik di Jakarta Selatan 2026" value={postForm.title} onChange={(e) => setPostForm({ ...postForm, title: e.target.value })} data-testid="post-title" />
            </Field>

            <Field
              label="URL Slug"
              hint="Kosongkan untuk membuat otomatis dari judul. Karakter yang diperbolehkan: huruf kecil, angka, dan tanda hubung (-). Karakter lain akan dinormalisasi saat disimpan."
            >
              <div className="flex items-stretch rounded-md border border-[#EAE7E0] overflow-hidden focus-within:border-[#1E3A8A]">
                <span className="inline-flex items-center px-3 bg-[#F3F1ED] text-[#4B5563] text-sm select-none border-r border-[#EAE7E0]">
                  /jurnal/
                </span>
                <Input
                  placeholder={(() => {
                    // Live preview of auto-slug so admin knows what will be used if left blank
                    const t = postForm.title || "";
                    return t ? t.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 60) : "url-otomatis-dari-judul";
                  })()}
                  value={postForm.slug || ""}
                  onChange={(e) => setPostForm({ ...postForm, slug: e.target.value })}
                  onBlur={(e) => {
                    // Normalize on blur to give admin instant feedback on what the server will store
                    const raw = e.target.value.toLowerCase().trim();
                    const norm = raw.replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 80);
                    if (norm !== raw) setPostForm({ ...postForm, slug: norm });
                  }}
                  data-testid="post-slug"
                  className="flex-1 border-0 focus-visible:ring-0 focus-visible:ring-offset-0"
                />
              </div>
            </Field>

            <Field label="Ringkasan (excerpt)" hint="Muncul di kartu artikel dan meta preview">
              <Textarea rows={2} placeholder="Ringkasan singkat 1–2 kalimat" value={postForm.excerpt} onChange={(e) => setPostForm({ ...postForm, excerpt: e.target.value })} data-testid="post-excerpt" />
            </Field>

            <Field
              label="The School Reports Takeaway"
              hint="Pesan editorial 2–4 kalimat yang ditampilkan sebagai kotak krem di atas daftar Tag. Kosongkan untuk menyembunyikan."
            >
              <Textarea
                rows={4}
                placeholder="Kesimpulan editorial dari redaksi. Ini muncul di kotak krem TSR Takeaway, terpisah dari bodi artikel utama."
                value={postForm.takeaway || ""}
                onChange={(e) => setPostForm({ ...postForm, takeaway: e.target.value })}
                data-testid="post-takeaway"
              />
            </Field>

            {/* Journal fields — content type + featured */}
            <div className="grid grid-cols-2 gap-3 items-center">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-[#4B5563] mb-1">Jenis konten</div>
                <select value={postForm.content_type}
                  onChange={(e) => setPostForm({ ...postForm, content_type: e.target.value })}
                  data-testid="post-content-type"
                  className="w-full h-10 px-3 rounded-md border border-[#EAE7E0] bg-white text-sm">
                  <option value="editorial">Editorial</option>
                  <option value="insight">Insight</option>
                  <option value="laporan">Laporan</option>
                  <option value="wawancara">Wawancara</option>
                  <option value="feature">Feature</option>
                </select>
              </div>
              <label className="inline-flex items-center gap-2 cursor-pointer mt-4">
                <input type="checkbox" checked={!!postForm.is_featured}
                  onChange={(e) => setPostForm({ ...postForm, is_featured: e.target.checked })}
                  data-testid="post-is-featured"
                  className="accent-[#1E3A8A]" />
                <span className="text-sm text-[#1F2937]">Tampilkan sebagai <b>Laporan Utama</b> (hero)</span>
              </label>
            </div>

            {/* Kategori editorial — multi-select (1..N dari 8 slug canonical).
                Backend menolak CREATE tanpa kategori; UPDATE dijaga oleh client
                validation di savePost() untuk mencegah artikel legacy diterbitkan
                ulang tanpa taksonomi. */}
            <div className="p-3 rounded-xl bg-[#F3F1ED] border border-[#EAE7E0]" data-testid="post-categories-section">
              <div className="flex items-center justify-between gap-2 mb-2">
                <div className="text-[10px] uppercase tracking-widest text-[#4B5563] font-bold">
                  Kategori editorial <span className="text-[#B91C1C] normal-case tracking-normal">*</span>
                </div>
                <div className="text-[11px] text-[#4B5563]">
                  {(postForm.categories || []).length === 0
                    ? "Belum dikategorikan · pilih minimal 1"
                    : `${postForm.categories.length} kategori dipilih`}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-x-3 gap-y-1.5">
                {JOURNAL_CATEGORIES.map((c) => {
                  const checked = (postForm.categories || []).includes(c.slug);
                  return (
                    <label key={c.slug} className="inline-flex items-center gap-2 cursor-pointer text-sm text-[#1F2937]">
                      <input
                        type="checkbox"
                        checked={checked}
                        data-testid={`post-category-${c.slug}`}
                        onChange={(e) => {
                          const next = new Set(postForm.categories || []);
                          if (e.target.checked) next.add(c.slug);
                          else next.delete(c.slug);
                          setPostForm({ ...postForm, categories: Array.from(next) });
                        }}
                        className="accent-[#1E3A8A]"
                      />
                      <span>{c.label}</span>
                    </label>
                  );
                })}
              </div>
            </div>

            {/* Sekolah Terkait — searchable multi-select. Reuses the admin's
                already-loaded `schools` list (from /admin/schools/all) so we
                don't fire a parallel search endpoint; case-insensitive match
                against name/city and de-duped by school_id. */}
            <div className="p-3 rounded-xl bg-white border border-[#EAE7E0]" data-testid="post-school-picker-section">
              <div className="text-[10px] uppercase tracking-widest text-[#4B5563] font-bold mb-1.5">
                Sekolah Terkait <span className="text-[#4B5563] normal-case tracking-normal font-normal">(opsional)</span>
              </div>
              <div className="flex flex-wrap gap-1.5 mb-2" data-testid="post-school-chips">
                {(postForm.school_ids || []).length === 0 ? (
                  <span className="text-[11px] italic text-[#4B5563]">Belum ada sekolah terkait.</span>
                ) : (
                  (postForm.school_ids || []).map((sid) => {
                    const s = schools.find((x) => x.school_id === sid);
                    const label = s?.name || sid;
                    return (
                      <span key={sid} className="inline-flex items-center gap-1 rounded-full pl-2 pr-1 py-0.5 text-xs bg-[#EEF2FF] text-[#1E3A8A] border border-[#C7D2FE]">
                        <span>{label}</span>
                        <button
                          type="button"
                          data-testid={`post-school-chip-remove-${sid}`}
                          onClick={() => setPostForm({ ...postForm, school_ids: (postForm.school_ids || []).filter((x) => x !== sid) })}
                          className="rounded-full hover:bg-[#C7D2FE] w-4 h-4 inline-flex items-center justify-center"
                          aria-label={`Hapus ${label}`}
                        >
                          <X size={10} />
                        </button>
                      </span>
                    );
                  })
                )}
              </div>
              <Input
                placeholder="Cari sekolah… (mis: SMA 68, Binus, Al Azhar)"
                value={schoolPickerQuery}
                onChange={(e) => setSchoolPickerQuery(e.target.value)}
                data-testid="post-school-search"
              />
              {schoolPickerQuery.trim().length >= 2 && (() => {
                const q = schoolPickerQuery.trim().toLowerCase();
                const selected = new Set(postForm.school_ids || []);
                const matches = schools
                  .filter((s) => {
                    if (selected.has(s.school_id)) return false;
                    const name = (s.name || "").toLowerCase();
                    const city = (s.city || "").toLowerCase();
                    return name.includes(q) || city.includes(q);
                  })
                  .slice(0, 8);
                return (
                  <div className="mt-2 border border-[#EAE7E0] rounded-lg bg-white max-h-48 overflow-y-auto" data-testid="post-school-suggestions">
                    {matches.length === 0 ? (
                      <div className="p-2 text-xs text-[#4B5563] italic">Tidak ada hasil untuk "{schoolPickerQuery}".</div>
                    ) : (
                      matches.map((s) => (
                        <button
                          key={s.school_id}
                          type="button"
                          data-testid={`post-school-suggestion-${s.school_id}`}
                          onClick={() => {
                            setPostForm({ ...postForm, school_ids: [...(postForm.school_ids || []), s.school_id] });
                            setSchoolPickerQuery("");
                          }}
                          className="block w-full text-left px-3 py-2 hover:bg-[#F3F1ED] text-sm"
                        >
                          <div className="text-[#1F2937]">{s.name}</div>
                          <div className="text-[11px] text-[#4B5563]">{[s.city, s.state].filter(Boolean).join(" · ") || s.school_id}</div>
                        </button>
                      ))
                    )}
                  </div>
                );
              })()}
            </div>

            <div data-testid="post-cover-wrap">
              <div className="text-[10px] uppercase tracking-widest text-[#4B5563] mb-1.5 font-bold">Gambar sampul (opsional)</div>
              <ImageUploader
                value={postForm.cover_url}
                onChange={(url) => setPostForm({ ...postForm, cover_url: url })}
                placeholder="URL gambar sampul, atau unggah dari perangkat"
                testid="post-cover"
                aspect="16/10"
              />
            </div>

            {/* Author profile fields */}
            <div className="grid grid-cols-2 gap-3">
              <Field label="Jabatan penulis" hint="Ditampilkan di bawah nama">
                <Input placeholder="mis: Kepala Riset" value={postForm.author_title || ""}
                  onChange={(e) => setPostForm({ ...postForm, author_title: e.target.value })}
                  data-testid="post-author-title" />
              </Field>
              <Field label="URL foto penulis" hint="Opsional — bisa dari CDN">
                <Input placeholder="https://.../avatar.jpg" value={postForm.author_avatar_url || ""}
                  onChange={(e) => setPostForm({ ...postForm, author_avatar_url: e.target.value })}
                  data-testid="post-author-avatar" />
              </Field>
            </div>

            <Field label="Estimasi waktu baca" hint="Dalam menit (1–120)">
              <Input type="number" min="1" max="120" placeholder="mis: 5" value={postForm.read_time_min || ""}
                onChange={(e) => setPostForm({ ...postForm, read_time_min: e.target.value ? parseInt(e.target.value, 10) : null })}
                data-testid="post-read-time" />
            </Field>

            {/* PDF upload (only relevant for 'laporan') */}
            {postForm.content_type === "laporan" && (
              <div className="p-3 rounded-xl bg-[#F6E7DF]/40 border border-[#F6E7DF] space-y-2" data-testid="post-pdf-section">
                <div className="text-xs font-semibold text-[#BE5730]">Unggah PDF laporan</div>
                <div className="flex items-center gap-2 flex-wrap">
                  <input type="file" accept="application/pdf"
                    data-testid="post-pdf-file"
                    onChange={async (e) => {
                      const f = e.target.files?.[0];
                      if (!f) return;
                      if (f.size > 25 * 1024 * 1024) { toast.error("Ukuran PDF melebihi 25 MB"); return; }
                      const fd = new FormData();
                      fd.append("file", f);
                      try {
                        const r = await api.post("/admin/upload/pdf", fd, { headers: { "Content-Type": "multipart/form-data" } });
                        setPostForm((p) => ({ ...p, pdf_url: r.data.url, pdf_size_bytes: r.data.size }));
                        toast.success("PDF terunggah");
                      } catch (err) { toast.error(err.response?.data?.detail || "Gagal upload PDF"); }
                    }} />
                  {postForm.pdf_url && (
                    <>
                      <a href={postForm.pdf_url} target="_blank" rel="noreferrer" className="text-xs text-[#1E3A8A] underline">Lihat PDF</a>
                      <span className="text-xs text-[#4B5563]">
                        {postForm.pdf_size_bytes ? `${(postForm.pdf_size_bytes / (1024 * 1024)).toFixed(1)} MB` : ""}
                      </span>
                      <button type="button" onClick={() => setPostForm({ ...postForm, pdf_url: "", pdf_size_bytes: null })}
                        data-testid="post-pdf-clear" className="text-xs text-red-600 hover:underline">Hapus</button>
                    </>
                  )}
                </div>
              </div>
            )}

            {/* Key stats editor (up to 4) */}
            <div className="p-3 rounded-xl bg-[#F3F1ED] border border-[#EAE7E0] space-y-2" data-testid="post-key-stats-section">
              <div className="flex items-center justify-between">
                <div className="text-xs font-semibold text-[#1F2937]">Statistik kunci (untuk kartu hero)</div>
                <button type="button"
                  disabled={(postForm.key_stats || []).length >= 4}
                  onClick={() => setPostForm({ ...postForm, key_stats: [...(postForm.key_stats || []), { value: "", label: "", unit: "" }] })}
                  data-testid="post-stat-add"
                  className="text-xs text-[#D2673E] hover:underline disabled:opacity-40 disabled:no-underline">
                  + Tambah statistik
                </button>
              </div>
              {(postForm.key_stats || []).map((s, i) => (
                <div key={i} className="grid grid-cols-[1fr_2fr_60px_auto] gap-2 items-center" data-testid={`post-stat-row-${i}`}>
                  <Input placeholder="Nilai (mis. 62)" value={s.value}
                    onChange={(e) => {
                      const ks = [...postForm.key_stats];
                      ks[i] = { ...ks[i], value: e.target.value };
                      setPostForm({ ...postForm, key_stats: ks });
                    }} className="h-9" />
                  <Input placeholder="Label (mis. sekolah swasta)" value={s.label}
                    onChange={(e) => {
                      const ks = [...postForm.key_stats];
                      ks[i] = { ...ks[i], label: e.target.value };
                      setPostForm({ ...postForm, key_stats: ks });
                    }} className="h-9" />
                  <Input placeholder="Unit" value={s.unit || ""}
                    onChange={(e) => {
                      const ks = [...postForm.key_stats];
                      ks[i] = { ...ks[i], unit: e.target.value };
                      setPostForm({ ...postForm, key_stats: ks });
                    }} className="h-9" />
                  <button type="button"
                    onClick={() => setPostForm({ ...postForm, key_stats: postForm.key_stats.filter((_, j) => j !== i) })}
                    data-testid={`post-stat-remove-${i}`}
                    className="text-xs text-red-600 hover:underline">Hapus</button>
                </div>
              ))}
              {(postForm.key_stats || []).length === 0 && (
                <div className="text-[11px] italic text-[#4B5563]">Belum ada statistik. Contoh: "62" · "sekolah swasta" · "%"</div>
              )}
            </div>

            <TagsInput
              value={postForm.tags}
              onChange={(next) => setPostForm({ ...postForm, tags: next })}
            />
            <div>
              <div className="text-[10px] uppercase tracking-widest text-[#4B5563] mb-1.5 font-bold">Isi artikel</div>
              <BlogEditor
                value={postForm.body}
                onChange={(html) => setPostForm({ ...postForm, body: html })}
                placeholder="Mulai menulis artikel di sini… Gunakan toolbar untuk judul, kutipan, dan daftar."
              />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => { setNewPostOpen(false); setEditingPost(null); setPostForm(emptyPost); }} className="rounded-full">Batal</Button>
              <Button
                variant="outline"
                onClick={previewPost}
                data-testid="post-preview"
                className="rounded-full border-[#EAE7E0] text-[#1B2B4D] hover:bg-[#F3F1ED]"
                title="Buka pratinjau di tab baru — persis seperti versi yang tayang"
              >
                <Eye size={14} className="mr-1.5" /> Lihat pratinjau
              </Button>
              <Button
                variant="outline"
                onClick={() => savePost(editingPost ? "edit" : "create", "draft")}
                data-testid="post-save-draft"
                className="rounded-full border-[#EAE7E0] text-[#8A5A00] bg-[#FFF8E6] hover:bg-[#FCEDBF] hover:text-[#8A5A00]"
                title="Simpan sebagai draf — tidak akan tayang di The Reports publik"
              >
                Simpan Draf
              </Button>
              <Button
                onClick={() => savePost(editingPost ? "edit" : "create", "published")}
                data-testid="post-save-publish"
                className="rounded-full bg-[#1E3A8A] hover:bg-[#1E40AF] text-white"
                title="Terbitkan artikel ke The Reports publik"
              >
                {editingPost && postForm.status === "published" ? "Simpan & Terbitkan Ulang" : "Terbitkan"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit school dialog */}
      {/* CSV bulk import dialog */}
      <Dialog open={importOpen} onOpenChange={(v) => { setImportOpen(v); if (!v) { setImportFile(null); setImportResult(null); } }}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Impor sekolah massal (CSV)</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-2" data-testid="import-schools-dialog">
            <p className="text-sm text-[#4B5563]">
              Unggah file CSV dengan header baris pertama. Kolom wajib: <code className="text-xs">name</code> dan <code className="text-xs">city</code>. Kolom multi-nilai (mis. <code className="text-xs">grade_levels</code>, <code className="text-xs">curriculum</code>, <code className="text-xs">extracurriculars</code>, <code className="text-xs">gallery_images</code>) dipisahkan tanda <code className="text-xs">|</code>.
            </p>
            <p className="text-xs text-[#4B5563] italic">
              Template CSV kini mendukung semua field yang tersedia di form "Tambah sekolah" — termasuk NPSN, akreditasi, biaya per-jenjang (SPP min/max), afiliasi agama, dan tautan hierarki (campus_id). Data <code>fee_structure</code> per-jenjang tetap harus disunting via UI setelah impor.
            </p>
            <button onClick={downloadImportTemplate} data-testid="import-template-btn" className="text-sm text-[#1E3A8A] hover:text-[#E07A5F] inline-flex items-center gap-1">
              <Download size={14} /> Unduh template CSV
            </button>
            <div>
              <input type="file" accept=".csv,text/csv" onChange={(e) => setImportFile(e.target.files?.[0] || null)} data-testid="import-file-input" className="block w-full text-sm text-[#4B5563] file:mr-3 file:px-4 file:py-2 file:rounded-full file:border-0 file:bg-[#EFF6FF] file:text-[#1E3A8A] file:cursor-pointer hover:file:bg-[#DBEAFE]" />
              {importFile && <div className="text-xs text-[#4B5563] mt-1">📎 {importFile.name} · {(importFile.size / 1024).toFixed(1)} KB</div>}
            </div>
            {importResult && (
              <div data-testid="import-result" className="bg-[#F0FDF4] border border-[#BBF7D0] rounded-xl p-3 text-sm">
                <div className="text-[#166534] font-semibold">✓ {importResult.created_count} sekolah berhasil dibuat</div>
                {importResult.error_count > 0 && (
                  <>
                    <div className="mt-2 text-[#B91C1C] text-xs">⚠ {importResult.error_count} baris gagal:</div>
                    <ul className="mt-1 text-[11px] text-[#4B5563] space-y-0.5 max-h-32 overflow-y-auto">
                      {importResult.errors.map((er, i) => (<li key={i}>Baris {er.row}: {er.reason}</li>))}
                    </ul>
                  </>
                )}
              </div>
            )}
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setImportOpen(false)} className="rounded-full">Tutup</Button>
              <Button onClick={submitImport} disabled={importBusy || !importFile} data-testid="import-submit-btn" className="rounded-full bg-[#1E3A8A] hover:bg-[#1E40AF] text-white">
                {importBusy ? "Memproses…" : "Impor sekarang"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* New school dialog (admin curated) */}
      <Dialog open={newSchoolOpen} onOpenChange={(v) => { setNewSchoolOpen(v); if (!v) { setNewSchoolForm(emptyNewSchool); setNewSchoolPhotos([]); } }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Tambah sekolah baru</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-2" data-testid="new-school-form">
            <Field label="Nama sekolah" required>
              <Input placeholder="mis: Sekolah Al-Azhar Kebayoran Baru" value={newSchoolForm.name} onChange={(e) => setNewSchoolForm({ ...newSchoolForm, name: e.target.value })} data-testid="new-school-name" />
            </Field>
            {/* Alias input sits directly under Nama Sekolah in BOTH forms so
                admins associate it with naming, not location metadata. Same
                comma-parsing logic used by the Edit dialog — single source of
                truth for the aliases[] payload. */}
            <Field label="Nama lain / alias pencarian" hint="Pisahkan dengan koma. Digunakan untuk membantu pencarian dan tidak mengubah nama resmi sekolah.">
              <AliasesInput
                value={newSchoolForm.aliases}
                onChange={(next) => setNewSchoolForm({ ...newSchoolForm, aliases: next })}
                testId="new-school-aliases"
              />
            </Field>
            <div className="grid grid-cols-2 gap-2">
              <Field label="Provinsi" required>
                <Select value={newSchoolForm.state || "none"} onValueChange={(v) => setNewSchoolForm({ ...newSchoolForm, state: v === "none" ? "" : v, city: "" })}>
                  <SelectTrigger data-testid="new-school-state"><SelectValue placeholder="Pilih provinsi" /></SelectTrigger>
                  <SelectContent className="max-h-80">
                    <SelectItem value="none">Pilih provinsi…</SelectItem>
                    {PROVINCE_NAMES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Kota / Kabupaten" required>
                {(getCitiesForProvince(newSchoolForm.state) || []).length > 0 ? (
                  <Select value={newSchoolForm.city || "none"} onValueChange={(v) => setNewSchoolForm({ ...newSchoolForm, city: v === "none" ? "" : v })}>
                    <SelectTrigger data-testid="new-school-city"><SelectValue placeholder="Pilih kota/kabupaten" /></SelectTrigger>
                    <SelectContent className="max-h-80">
                      <SelectItem value="none">Pilih kota/kabupaten…</SelectItem>
                      {getCitiesForProvince(newSchoolForm.state).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                ) : (
                  <Input placeholder={newSchoolForm.state ? "Ketik nama kota/kabupaten" : "Pilih provinsi dulu"}
                    value={newSchoolForm.city}
                    onChange={(e) => setNewSchoolForm({ ...newSchoolForm, city: e.target.value })}
                    disabled={!newSchoolForm.state}
                    data-testid="new-school-city" />
                )}
              </Field>
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-widest text-[#1E3A8A] font-semibold mb-2 font-sans">Alamat lengkap</div>
              <Field label="Alamat jalan">
                <Input placeholder="mis: Jl. Sisingamangaraja No. 62" value={newSchoolForm.address_street} onChange={(e) => setNewSchoolForm({ ...newSchoolForm, address_street: e.target.value })} data-testid="new-school-address-street" />
              </Field>
              <div className="grid grid-cols-2 gap-2 mt-2">
                <Field label="Kelurahan">
                  <Input placeholder="mis: Kebayoran Baru" value={newSchoolForm.kelurahan} onChange={(e) => setNewSchoolForm({ ...newSchoolForm, kelurahan: e.target.value })} data-testid="new-school-kelurahan" />
                </Field>
                <Field label="Kecamatan">
                  <Input placeholder="mis: Kebayoran Baru" value={newSchoolForm.kecamatan} onChange={(e) => setNewSchoolForm({ ...newSchoolForm, kecamatan: e.target.value })} data-testid="new-school-kecamatan" />
                </Field>
              </div>
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-widest text-[#1E3A8A] font-semibold mb-2 font-sans">Jenjang pendidikan yang ditawarkan</div>
              <div className="flex flex-wrap gap-2">
                {EDU_LEVEL_OPTIONS.map((g) => (
                  <button type="button" key={g.value} onClick={() => toggleNewSchoolEduLevel(g.value)} data-testid={`new-school-edu-level-${g.value}`}
                    className={"px-3 py-1.5 rounded-full text-xs border transition-colors " + (newSchoolForm.education_levels.includes(g.value) ? "bg-[#1E3A8A] text-white border-[#1E3A8A]" : "bg-white text-[#4B5563] border-[#EAE7E0] hover:bg-[#F3F1ED]")}>
                    {g.label}
                  </button>
                ))}
              </div>
            </div>
            <Field label="Tipe sekolah" required>
              <Select value={newSchoolForm.school_type} onValueChange={(v) => setNewSchoolForm({ ...newSchoolForm, school_type: v })}>
                <SelectTrigger data-testid="new-school-type"><SelectValue placeholder="Pilih tipe sekolah" /></SelectTrigger>
                <SelectContent>
                  {TYPE_OPTIONS.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Afiliasi agama" hint="Membantu orang tua memfilter sekolah sesuai keyakinan">
              <Select value={newSchoolForm.religion_affiliation || "none"} onValueChange={(v) => setNewSchoolForm({ ...newSchoolForm, religion_affiliation: v === "none" ? "" : v })}>
                <SelectTrigger data-testid="new-school-religion"><SelectValue placeholder="Pilih afiliasi agama" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Tidak ditentukan</SelectItem>
                  {RELIGION_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <div className="rounded-xl border border-[#EAE7E0] bg-white p-3">
              <CurriculumPicker
                value={newSchoolForm.curriculum}
                onChange={(next) => setNewSchoolForm({ ...newSchoolForm, curriculum: next })}
                testidPrefix="new-school-curriculum"
              />
              <div className="mt-3">
                <label className="text-[10px] uppercase tracking-widest text-[#1E3A8A] font-semibold mb-1 block">
                  Detail Kurikulum
                </label>
                <Textarea
                  value={newSchoolForm.curriculum_detail || ""}
                  onChange={(e) => setNewSchoolForm({ ...newSchoolForm, curriculum_detail: e.target.value })}
                  placeholder="Contoh: Cambridge Lower Secondary & Kurikulum Merdeka; IB PYP di jenjang SD"
                  className="rounded-xl border-[#EAE7E0] min-h-[70px]"
                  data-testid="new-school-curriculum-detail"
                />
                <div className="text-[10px] text-[#4B5563] mt-1">
                  Deskripsi spesifik hanya untuk profil sekolah. Tidak dipakai sebagai filter di Direktori.
                </div>
              </div>
            </div>

            {/* Brand & multi-cabang */}
            <div className="rounded-xl border border-[#DBEAFE] bg-[#EFF6FF] p-3 space-y-2" data-testid="new-school-brand-block">
              <div className="text-[10px] uppercase tracking-widest text-[#1E3A8A] font-semibold">Brand & Cabang</div>
              <div className="grid grid-cols-2 gap-2">
                <Field label="Cabang (Campus)" hint="Pilih campus tempat unit ini bernaung. Buat campus baru via menu 'Grup & Cabang'.">
                  <CampusPicker
                    value={newSchoolForm.campus_id}
                    onChange={(id) => setNewSchoolForm({ ...newSchoolForm, campus_id: id, brand_id: id || newSchoolForm.brand_id })}
                    testid="new-school-campus-picker"
                  />
                </Field>
                <Field label="Nama cabang (label ringkas)">
              <Input placeholder="mis: Kebayoran" value={newSchoolForm.branch_name} onChange={(e) => setNewSchoolForm({ ...newSchoolForm, branch_name: e.target.value })} data-testid="new-school-branch-name" />
                </Field>
              </div>
              <div className="text-[10px] text-[#4B5563]">Kosongkan Cabang untuk sekolah mandiri (tidak ada Campus induk).</div>
            </div>

            {/* Struktur biaya */}
            <div className="rounded-xl border border-[#FDE68A] bg-[#FFFBEB] p-3 space-y-2" data-testid="new-school-cost-block">
              <div className="text-[10px] uppercase tracking-widest text-[#92400E] font-semibold">Struktur Biaya — Range (Rp)</div>
              <Field label="Tahun Ajaran Biaya" hint="Format: 2026/2027 — membantu orang tua tahu tahun referensi">
                <Input placeholder="mis: 2026/2027" value={newSchoolForm.fee_academic_year || ""}
                  onChange={(e) => setNewSchoolForm({ ...newSchoolForm, fee_academic_year: e.target.value })}
                  data-testid="new-school-fee-academic-year" />
              </Field>
              <div className="grid grid-cols-2 gap-2">
                <Field label="Uang pangkal – MIN" hint="Rupiah (batas bawah)">
                  <MoneyInput placeholder="mis: 20.000.000" value={newSchoolForm.entry_fee_min}
                    onChange={(n) => setNewSchoolForm({ ...newSchoolForm, entry_fee_min: n ?? "" })}
                    data-testid="new-school-entry-fee-min" />
                </Field>
                <Field label="Uang pangkal – MAX" hint="Rupiah (batas atas)">
                  <MoneyInput placeholder="mis: 30.000.000" value={newSchoolForm.entry_fee_max}
                    onChange={(n) => setNewSchoolForm({ ...newSchoolForm, entry_fee_max: n ?? "" })}
                    data-testid="new-school-entry-fee-max" />
                </Field>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Field label="SPP / bulan – MIN" hint="Rupiah (batas bawah)">
                  <MoneyInput placeholder="mis: 4.500.000" value={newSchoolForm.spp_min}
                    onChange={(n) => setNewSchoolForm({ ...newSchoolForm, spp_min: n ?? "" })}
                    data-testid="new-school-spp-min" />
                </Field>
                <Field label="SPP / bulan – MAX" hint="Rupiah (batas atas)">
                  <MoneyInput placeholder="mis: 6.500.000" value={newSchoolForm.spp_max}
                    onChange={(n) => setNewSchoolForm({ ...newSchoolForm, spp_max: n ?? "" })}
                    data-testid="new-school-spp-max" />
                </Field>
              </div>
              <Field label="Biaya tahunan lain" hint="Rupiah — seragam, buku, kegiatan">
                <MoneyInput placeholder="mis: 3.000.000" value={newSchoolForm.other_annual_fees}
                  onChange={(n) => setNewSchoolForm({ ...newSchoolForm, other_annual_fees: n ?? "" })}
                  data-testid="new-school-other-annual" />
              </Field>
              {(() => {
                const lo = Number(newSchoolForm.spp_min); const hi = Number(newSchoolForm.spp_max);
                const est = lo > 0 && hi > 0 ? Math.round((lo + hi) / 2) : (lo > 0 ? lo : null);
                return est ? (
                  <div className="text-[11px] text-[#92400E] bg-[#FEF3C7] rounded-lg px-2 py-1.5" data-testid="new-school-estimated-spp">
                    <strong>Estimasi SPP otomatis:</strong> Rp {est.toLocaleString("id-ID")} / bulan <span className="text-[#4B5563]">(midpoint MIN &amp; MAX — dihitung sistem)</span>
                  </div>
                ) : null;
              })()}
              <div className="text-[10px] text-[#4B5563]">Isi dalam Rupiah tanpa titik/koma — sistem menambahkan pemisah ribuan otomatis. "Estimasi SPP" dihitung otomatis oleh sistem sebagai midpoint MIN &amp; MAX.</div>
            </div>

            {/* Indikator performa */}
            <div className="rounded-xl border border-[#A7F3D0] bg-[#ECFDF5] p-3 space-y-2" data-testid="new-school-perf-block">
              <div className="text-[10px] uppercase tracking-widest text-[#065F46] font-semibold">Indikator Performa Akademik</div>
              <div className="grid grid-cols-3 gap-2">
                <Field label="Akreditasi">
                  <Select value={newSchoolForm.accreditation_grade || "none"} onValueChange={(v) => setNewSchoolForm({ ...newSchoolForm, accreditation_grade: v === "none" ? "" : v })}>
                    <SelectTrigger data-testid="new-school-accreditation"><SelectValue placeholder="Pilih grade" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Belum diakreditasi</SelectItem>
                      <SelectItem value="A">A — Sangat Baik</SelectItem>
                      <SelectItem value="B">B — Baik</SelectItem>
                      <SelectItem value="C">C — Cukup</SelectItem>
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Tahun terakreditasi" hint="mis: 2023">
                  <Input type="number" min="1990" max="2100" placeholder="mis: 2023" value={newSchoolForm.accreditation_year} onChange={(e) => setNewSchoolForm({ ...newSchoolForm, accreditation_year: e.target.value })} data-testid="new-school-accreditation-year" />
                </Field>
                <Field label="Rasio guru : siswa" hint="Otomatis dari jumlah">
                  <Input placeholder="mis: 1:15" value={newSchoolForm.teacher_to_student_ratio} onChange={(e) => setNewSchoolForm({ ...newSchoolForm, teacher_to_student_ratio: e.target.value })} data-testid="new-school-ratio" />
                </Field>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Field label="Jumlah siswa" hint="Total keseluruhan">
                  <Input type="number" min="0" placeholder="mis: 850" value={newSchoolForm.students_count} onChange={(e) => setNewSchoolForm({ ...newSchoolForm, students_count: e.target.value })} data-testid="new-school-students-count" />
                </Field>
                <Field label="Jumlah guru" hint="Total tenaga pengajar">
                  <Input type="number" min="0" placeholder="mis: 55" value={newSchoolForm.teachers_count} onChange={(e) => setNewSchoolForm({ ...newSchoolForm, teachers_count: e.target.value })} data-testid="new-school-teachers-count" />
                </Field>
              </div>
              <Field label="Prestasi alumni" hint="Pisahkan dengan koma atau baris baru; muncul sebagai kartu cerita">
                <Textarea rows={3} placeholder="mis: 3 alumni diterima di ITB tahun 2024, juara olimpiade sains nasional, alumni bersekolah di NUS…" value={newSchoolForm.alumni_achievements} onChange={(e) => setNewSchoolForm({ ...newSchoolForm, alumni_achievements: e.target.value })} data-testid="new-school-alumni" />
              </Field>
            </div>

            {/* NPSN — verifikasi Kemdikbud */}
            <NpsnVerifier
              value={newSchoolForm.npsn}
              onChange={(v) => setNewSchoolForm({ ...newSchoolForm, npsn: v })}
              onLookup={(payload) => {
                // Auto-fill missing address/coord fields from Kemdikbud snapshot
                const k = payload.kemdikbud || {};
                setNewSchoolForm((prev) => ({
                  ...prev,
                  name: prev.name || k.sekolah || "",
                  address_street: prev.address_street || k.alamat_jalan || "",
                  kecamatan: prev.kecamatan || (k.kecamatan || "").replace(/^Kec\.\s*/, ""),
                  city: prev.city || (k.kabupaten_kota || ""),
                  state: prev.state || (k.propinsi || "").replace(/^Prov\.\s*/, ""),
                  latitude: prev.latitude || (k.lintang ?? ""),
                  longitude: prev.longitude || (k.bujur ?? ""),
                }));
              }}
              testidPrefix="new-school-npsn"
            />

            <Field label="Deskripsi sekolah">
              <Textarea rows={3} placeholder="Ceritakan visi, misi, keunggulan sekolah dalam 2-3 paragraf" value={newSchoolForm.description} onChange={(e) => setNewSchoolForm({ ...newSchoolForm, description: e.target.value })} data-testid="new-school-description" />
            </Field>
            <Field label="Website sekolah">
              <Input placeholder="https://sekolah.sch.id" value={newSchoolForm.website} onChange={(e) => setNewSchoolForm({ ...newSchoolForm, website: e.target.value })} data-testid="new-school-website" />
            </Field>
            <div className="grid grid-cols-2 gap-2">
              <Field label="Latitude (koordinat peta)" hint="Terisi otomatis dari link Google Maps di bawah">
                <Input placeholder="mis: -6.2434" value={newSchoolForm.latitude} onChange={(e) => setNewSchoolForm({ ...newSchoolForm, latitude: e.target.value })} data-testid="new-school-latitude" readOnly />
              </Field>
              <Field label="Longitude (koordinat peta)" hint="Terisi otomatis dari link Google Maps di bawah">
                <Input placeholder="mis: 106.8006" value={newSchoolForm.longitude} onChange={(e) => setNewSchoolForm({ ...newSchoolForm, longitude: e.target.value })} data-testid="new-school-longitude" readOnly />
              </Field>
            </div>
            <GmapsLocationInput
              lat={newSchoolForm.latitude}
              lng={newSchoolForm.longitude}
              onCoords={(lat, lng) => setNewSchoolForm((prev) => ({ ...prev, latitude: lat ?? "", longitude: lng ?? "" }))}
              testidPrefix="new-school-gmaps"
            />
            <ImageCropUploader
              value={newSchoolForm.image_url || ""}
              onChange={(url) => setNewSchoolForm({ ...newSchoolForm, image_url: url })}
              aspect={16 / 9}
              label="Foto utama sekolah"
              testidPrefix="new-school-crop"
            />
            <div>
              <div className="text-[11px] uppercase tracking-widest text-[#1E3A8A] font-semibold mb-2 font-sans">Galeri foto tambahan (opsional)</div>
              <PhotoUploader value={newSchoolPhotos} onChange={setNewSchoolPhotos} maxPhotos={10} testidPrefix="new-school-photo" label="Unggah foto galeri" />
            </div>
            <div className="flex justify-end gap-2 pt-2 sticky bottom-0 bg-white pb-1">
              <Button variant="outline" onClick={() => setNewSchoolOpen(false)} className="rounded-full">Batal</Button>
              <Button onClick={createSchool} data-testid="new-school-submit" className="rounded-full bg-[#1E3A8A] hover:bg-[#1E40AF] text-white">
                <Plus size={14} className="mr-1" /> Tambah sekolah
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!editingSchool} onOpenChange={(v) => !v && setEditingSchool(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>Edit sekolah</DialogTitle></DialogHeader>
          {editingSchool && (
            <div className="space-y-3 mt-2 max-h-[70vh] overflow-y-auto pr-1">
              <Field label="Nama sekolah" required>
                <Input placeholder="mis: Sekolah Al-Azhar Kebayoran Baru" value={editingSchool.name || ""} onChange={(e) => setEditingSchool({ ...editingSchool, name: e.target.value })} data-testid="edit-name" />
              </Field>
              {/* Existing aliases[] hydrated from the doc — populates on Edit;
                  saves through the shared PUT payload, which the backend
                  normalizes via `_normalize_aliases`. No new schema. */}
              <Field label="Nama lain / alias pencarian" hint="Pisahkan dengan koma. Digunakan untuk membantu pencarian dan tidak mengubah nama resmi sekolah.">
                <AliasesInput
                  value={editingSchool.aliases}
                  onChange={(next) => setEditingSchool({ ...editingSchool, aliases: next })}
                  testId="edit-school-aliases"
                />
              </Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Provinsi">
                  <Select value={editingSchool.state || "none"} onValueChange={(v) => setEditingSchool({ ...editingSchool, state: v === "none" ? "" : v, city: "" })}>
                    <SelectTrigger data-testid="edit-state"><SelectValue placeholder="Pilih provinsi" /></SelectTrigger>
                    <SelectContent className="max-h-80">
                      <SelectItem value="none">Pilih provinsi…</SelectItem>
                      {PROVINCE_NAMES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Kota / Kabupaten">
                  {(getCitiesForProvince(editingSchool.state) || []).length > 0 ? (
                    <Select value={editingSchool.city || "none"} onValueChange={(v) => setEditingSchool({ ...editingSchool, city: v === "none" ? "" : v })}>
                      <SelectTrigger data-testid="edit-city"><SelectValue placeholder="Pilih kota/kabupaten" /></SelectTrigger>
                      <SelectContent className="max-h-80">
                        <SelectItem value="none">Pilih kota/kabupaten…</SelectItem>
                        {getCitiesForProvince(editingSchool.state).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  ) : (
                    <Input placeholder={editingSchool.state ? "Ketik nama kota/kabupaten" : "Pilih provinsi dulu"}
                      value={editingSchool.city || ""}
                      onChange={(e) => setEditingSchool({ ...editingSchool, city: e.target.value })}
                      disabled={!editingSchool.state}
                      data-testid="edit-city" />
                  )}
                </Field>
              </div>
              <div>
                <div className="text-[11px] uppercase tracking-widest text-[#1E3A8A] font-semibold mb-2 font-sans">Alamat lengkap</div>
                <Field label="Alamat jalan">
                  <Input placeholder="mis: Jl. Sisingamangaraja No. 62" value={editingSchool.address_street || ""} onChange={(e) => setEditingSchool({ ...editingSchool, address_street: e.target.value })} data-testid="edit-address-street" />
                </Field>
                <div className="grid grid-cols-2 gap-2 mt-2">
                  <Field label="Kelurahan">
                    <Input placeholder="mis: Kebayoran Baru" value={editingSchool.kelurahan || ""} onChange={(e) => setEditingSchool({ ...editingSchool, kelurahan: e.target.value })} data-testid="edit-kelurahan" />
                  </Field>
                  <Field label="Kecamatan">
                    <Input placeholder="mis: Kebayoran Baru" value={editingSchool.kecamatan || ""} onChange={(e) => setEditingSchool({ ...editingSchool, kecamatan: e.target.value })} data-testid="edit-kecamatan" />
                  </Field>
                </div>
              </div>
              <div>
                <div className="text-[11px] uppercase tracking-widest text-[#1E3A8A] font-semibold mb-2 font-sans">Jenjang pendidikan yang ditawarkan</div>
                <div className="flex flex-wrap gap-2" role="radiogroup" aria-label="Jenjang">
                  {EDU_LEVEL_OPTIONS.map((g) => {
                    // Single-select: this unit represents ONE jenjang. Whichever chip is picked
                    // replaces both the education_levels (uppercase for display) and grade_levels
                    // (lowercase for the public filter) arrays — no additive toggle.
                    const gLower = g.value.toLowerCase();
                    const active =
                      (editingSchool.education_levels || []).includes(g.value) ||
                      (editingSchool.grade_levels || []).includes(gLower);
                    return (
                      <button
                        type="button"
                        key={g.value}
                        role="radio"
                        aria-checked={active}
                        onClick={() => {
                          setEditingSchool({
                            ...editingSchool,
                            education_levels: active ? [] : [g.value],
                            grade_levels: active ? [] : [gLower],
                          });
                        }}
                        data-testid={`edit-edu-level-${g.value}`}
                        className={"px-3 py-1.5 rounded-full text-xs border transition-colors " + (active ? "bg-[#1E3A8A] text-white border-[#1E3A8A]" : "bg-white text-[#4B5563] border-[#EAE7E0] hover:bg-[#F3F1ED]")}>
                        {g.label}
                      </button>
                    );
                  })}
                </div>
                <div className="mt-1.5 text-[11px] text-[#8A8F99] italic">
                  Pilih <b>satu</b> jenjang saja untuk School Unit ini (TK / SD / SMP / SMA). Multi-jenjang di-model sebagai beberapa unit dalam satu Campus.
                </div>
              </div>
              <Field label="Tipe sekolah">
                <Select value={editingSchool.school_type || ""} onValueChange={(v) => setEditingSchool({ ...editingSchool, school_type: v })}>
                  <SelectTrigger data-testid="edit-type"><SelectValue placeholder="Pilih tipe" /></SelectTrigger>
                  <SelectContent>
                    {TYPE_OPTIONS.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Afiliasi agama" hint="Membantu orang tua memfilter sekolah sesuai keyakinan">
                <Select value={editingSchool.religion_affiliation || "none"} onValueChange={(v) => setEditingSchool({ ...editingSchool, religion_affiliation: v === "none" ? "" : v })}>
                  <SelectTrigger data-testid="edit-religion"><SelectValue placeholder="Pilih afiliasi agama" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Tidak ditentukan</SelectItem>
                    {RELIGION_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
              <div className="rounded-xl border border-[#FDE68A] bg-[#FFFBEB] p-3 space-y-2" data-testid="edit-cost-block">
                <div className="text-[10px] uppercase tracking-widest text-[#92400E] font-semibold">Struktur Biaya — Range (Rp)</div>
                <Field label="Tahun Ajaran Biaya" hint="Format: 2026/2027 — membantu orang tua tahu tahun referensi">
                  <Input placeholder="mis: 2026/2027" value={editingSchool.fee_academic_year || ""}
                    onChange={(e) => setEditingSchool({ ...editingSchool, fee_academic_year: e.target.value })}
                    data-testid="edit-fee-academic-year" />
                </Field>
                <div className="grid grid-cols-2 gap-2">
                  <Field label="Uang pangkal – MIN">
                    <MoneyInput placeholder="mis: 20.000.000" value={editingSchool.entry_fee_min}
                      onChange={(n) => setEditingSchool({ ...editingSchool, entry_fee_min: n })}
                      data-testid="edit-entry-fee-min" />
                  </Field>
                  <Field label="Uang pangkal – MAX">
                    <MoneyInput placeholder="mis: 30.000.000" value={editingSchool.entry_fee_max}
                      onChange={(n) => setEditingSchool({ ...editingSchool, entry_fee_max: n })}
                      data-testid="edit-entry-fee-max" />
                  </Field>
                  <Field label="SPP / bulan – MIN">
                    <MoneyInput placeholder="mis: 4.500.000" value={editingSchool.spp_min}
                      onChange={(n) => setEditingSchool({ ...editingSchool, spp_min: n })}
                      data-testid="edit-spp-min" />
                  </Field>
                  <Field label="SPP / bulan – MAX">
                    <MoneyInput placeholder="mis: 6.500.000" value={editingSchool.spp_max}
                      onChange={(n) => setEditingSchool({ ...editingSchool, spp_max: n })}
                      data-testid="edit-spp-max" />
                  </Field>
                </div>
                <Field label="Biaya tahunan lain" hint="Rp — seragam, buku, kegiatan">
                  <MoneyInput placeholder="mis: 3.000.000" value={editingSchool.other_annual_fees}
                    onChange={(n) => setEditingSchool({ ...editingSchool, other_annual_fees: n })}
                    data-testid="edit-other-annual" />
                </Field>
                {(() => {
                  const lo = Number(editingSchool.spp_min); const hi = Number(editingSchool.spp_max);
                  const est = lo > 0 && hi > 0 ? Math.round((lo + hi) / 2) : (lo > 0 ? lo : (editingSchool.estimated_spp || null));
                  return est ? (
                    <div className="text-[11px] text-[#92400E] bg-[#FEF3C7] rounded-lg px-2 py-1.5" data-testid="edit-estimated-spp">
                      <strong>Estimasi SPP otomatis:</strong> Rp {est.toLocaleString("id-ID")} / bulan <span className="text-[#4B5563]">(midpoint MIN &amp; MAX — dihitung sistem)</span>
                    </div>
                  ) : null;
                })()}
                {editingSchool.fees_last_updated_at ? (
                  <div className="text-[10px] text-[#4B5563]" data-testid="edit-fees-last-updated">
                    Terakhir diperbarui: {new Date(editingSchool.fees_last_updated_at).toLocaleDateString("id-ID", { day: "numeric", month: "long", year: "numeric" })}
                  </div>
                ) : null}
                <div className="text-[10px] text-[#4B5563]">Sistem menambahkan pemisah ribuan otomatis. "Estimasi SPP" &amp; "Estimasi tahun pertama" dihitung sistem.</div>
              </div>
              <Field label="Latitude (koordinat peta)" hint="Terisi otomatis dari link Google Maps di bawah">
                <Input placeholder="mis: -6.2434" value={editingSchool.latitude ?? ""} onChange={(e) => setEditingSchool({ ...editingSchool, latitude: e.target.value === "" ? null : (isNaN(Number(e.target.value)) ? e.target.value : Number(e.target.value)) })} data-testid="edit-latitude" readOnly />
              </Field>
              <Field label="Longitude (koordinat peta)" hint="Terisi otomatis dari link Google Maps di bawah">
                <Input placeholder="mis: 106.8006" value={editingSchool.longitude ?? ""} onChange={(e) => setEditingSchool({ ...editingSchool, longitude: e.target.value === "" ? null : (isNaN(Number(e.target.value)) ? e.target.value : Number(e.target.value)) })} data-testid="edit-longitude" readOnly />
              </Field>
              <GmapsLocationInput
                lat={editingSchool.latitude ?? ""}
                lng={editingSchool.longitude ?? ""}
                onCoords={(lat, lng) => setEditingSchool({ ...editingSchool, latitude: lat, longitude: lng })}
                testidPrefix="edit-gmaps"
              />
              <Field label="Subscription tier" hint="Premium mendapat badge partner & fitur eksklusif">
                <Select value={editingSchool.subscription_tier || "basic"} onValueChange={(v) => setEditingSchool({ ...editingSchool, subscription_tier: v })}>
                  <SelectTrigger data-testid="edit-tier"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="basic">Basic (default)</SelectItem>
                    <SelectItem value="premium">Premium (partner)</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Website sekolah">
                <Input placeholder="https://sekolah.sch.id" value={editingSchool.website || ""} onChange={(e) => setEditingSchool({ ...editingSchool, website: e.target.value })} data-testid="edit-website" />
              </Field>
              <ImageCropUploader
                value={editingSchool.image_url || ""}
                onChange={(url) => setEditingSchool({ ...editingSchool, image_url: url })}
                aspect={16 / 9}
                label="Foto utama sekolah"
                testidPrefix="edit-image-crop"
              />
              <Field label="Deskripsi sekolah">
                <Textarea rows={3} placeholder="Ceritakan visi, misi, keunggulan sekolah" value={editingSchool.description || ""} onChange={(e) => setEditingSchool({ ...editingSchool, description: e.target.value })} data-testid="edit-desc" />
              </Field>

              {/* Brand & multi-cabang */}
              <div className="rounded-xl border border-[#DBEAFE] bg-[#EFF6FF] p-3 space-y-3" data-testid="edit-school-brand-block">
                <div className="text-[11px] uppercase tracking-widest text-[#1E3A8A] font-semibold font-sans">Brand & Cabang</div>
                <div className="grid grid-cols-2 gap-2">
                  <Field label="Cabang (Campus)" hint="Pilih campus tempat unit ini bernaung.">
                    <CampusPicker
                      value={editingSchool.campus_id || null}
                      onChange={(id) => setEditingSchool({ ...editingSchool, campus_id: id, brand_id: id || editingSchool.brand_id })}
                      testid="edit-campus-picker"
                    />
                  </Field>
                  <Field label="Nama cabang (label ringkas)">
                    <Input placeholder="mis: Kebayoran" value={editingSchool.branch_name || ""} onChange={(e) => setEditingSchool({ ...editingSchool, branch_name: e.target.value })} data-testid="edit-branch-name" />
                  </Field>
                </div>
                <div className="text-[10px] text-[#4B5563]">Kosongkan Cabang untuk sekolah mandiri.</div>
              </div>

              {/* Kurikulum (multi-select) */}
              <div className="rounded-xl border border-[#EAE7E0] bg-white p-3" data-testid="edit-school-curriculum-block">
                <CurriculumPicker
                  value={editingSchool.curriculum || []}
                  onChange={(next) => setEditingSchool({ ...editingSchool, curriculum: next })}
                  testidPrefix="edit-curriculum"
                />
                <div className="mt-3">
                  <label className="text-[10px] uppercase tracking-widest text-[#1E3A8A] font-semibold mb-1 block">
                    Detail Kurikulum
                  </label>
                  <Textarea
                    value={editingSchool.curriculum_detail || ""}
                    onChange={(e) => setEditingSchool({ ...editingSchool, curriculum_detail: e.target.value })}
                    placeholder="Contoh: Cambridge Lower Secondary & Kurikulum Merdeka; IB PYP di jenjang SD"
                    className="rounded-xl border-[#EAE7E0] min-h-[70px]"
                    data-testid="edit-school-curriculum-detail"
                  />
                  <div className="text-[10px] text-[#4B5563] mt-1">
                    Deskripsi spesifik hanya untuk profil sekolah. Tidak dipakai sebagai filter di Direktori.
                  </div>
                </div>
              </div>

              {/* Tabel biaya per jenjang (multi-level TK/SD/SMP/SMA) */}
              <div className="rounded-xl border border-[#FDE68A] bg-[#FFFBEB] p-3 space-y-2" data-testid="edit-school-fee-structure-block">
                <div className="text-[10px] uppercase tracking-widest text-[#92400E] font-semibold">Tabel Biaya per Jenjang <span className="normal-case tracking-normal text-[10px] text-[#4B5563] font-normal">(untuk sekolah multi-level: TK/SD/SMP/SMA)</span></div>
                <div data-testid="edit-fee-structure-editor">
                  <div className="flex items-center justify-end mb-2">
                    <button type="button"
                      onClick={() => setEditingSchool({ ...editingSchool, fee_structure: [...(editingSchool.fee_structure || []), { jenjang: "", uang_pangkal: null, spp_bulanan: null }] })}
                      data-testid="edit-fee-row-add"
                      className="text-xs text-[#92400E] font-semibold hover:underline">
                      + Tambah baris jenjang
                    </button>
                  </div>
                  {(editingSchool.fee_structure || []).length === 0 ? (
                    <div className="text-[11px] italic text-[#4B5563]">Belum ada baris. Klik "+ Tambah baris jenjang" untuk membuat tabel perbandingan (mis. TK, SD, SMP, SMA). Untuk sekolah satu jenjang, cukup gunakan "Struktur Biaya — Range" di atas.</div>
                  ) : (
                    <div className="space-y-2">
                      {(editingSchool.fee_structure || []).map((r, i) => (
                        <div key={i} data-testid={`edit-fee-row-${i}`} className="grid grid-cols-[110px_1fr_1fr_28px] gap-2 items-center">
                          <Select value={(r.jenjang || "").toUpperCase() || "__pick"}
                            onValueChange={(v) => {
                              const fs = [...(editingSchool.fee_structure || [])];
                              fs[i] = { ...fs[i], jenjang: v === "__pick" ? "" : v };
                              setEditingSchool({ ...editingSchool, fee_structure: fs });
                            }}>
                            <SelectTrigger className="h-9 text-xs"><SelectValue placeholder="Jenjang" /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="__pick">— pilih —</SelectItem>
                              <SelectItem value="TK">TK</SelectItem>
                              <SelectItem value="SD">SD</SelectItem>
                              <SelectItem value="SMP">SMP</SelectItem>
                              <SelectItem value="SMA">SMA</SelectItem>
                              <SelectItem value="SMK">SMK</SelectItem>
                            </SelectContent>
                          </Select>
                          <MoneyInput placeholder="Uang pangkal" value={r.uang_pangkal}
                            onChange={(n) => {
                              const fs = [...(editingSchool.fee_structure || [])];
                              fs[i] = { ...fs[i], uang_pangkal: n };
                              setEditingSchool({ ...editingSchool, fee_structure: fs });
                            }}
                            className="h-9 text-xs" data-testid={`edit-fee-row-${i}-uang-pangkal`} />
                          <MoneyInput placeholder="SPP / bulan" value={r.spp_bulanan}
                            onChange={(n) => {
                              const fs = [...(editingSchool.fee_structure || [])];
                              fs[i] = { ...fs[i], spp_bulanan: n };
                              setEditingSchool({ ...editingSchool, fee_structure: fs });
                            }}
                            className="h-9 text-xs" data-testid={`edit-fee-row-${i}-spp`} />
                          <button type="button"
                            onClick={() => setEditingSchool({ ...editingSchool, fee_structure: editingSchool.fee_structure.filter((_, j) => j !== i) })}
                            data-testid={`edit-fee-row-${i}-remove`}
                            title="Hapus baris"
                            className="w-7 h-7 rounded-full text-red-600 hover:bg-red-50 flex items-center justify-center">
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Indikator performa */}
              <div className="rounded-xl border border-[#A7F3D0] bg-[#ECFDF5] p-3 space-y-2" data-testid="edit-school-perf-block">
                <div className="text-[10px] uppercase tracking-widest text-[#065F46] font-semibold">Indikator Performa Akademik</div>
                <div className="grid grid-cols-3 gap-2">
                  <Field label="Akreditasi">
                    <Select value={editingSchool.accreditation_grade || "none"} onValueChange={(v) => setEditingSchool({ ...editingSchool, accreditation_grade: v === "none" ? "" : v })}>
                      <SelectTrigger data-testid="edit-accreditation"><SelectValue placeholder="Pilih grade" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Belum diakreditasi</SelectItem>
                        <SelectItem value="A">A — Sangat Baik</SelectItem>
                        <SelectItem value="B">B — Baik</SelectItem>
                        <SelectItem value="C">C — Cukup</SelectItem>
                      </SelectContent>
                    </Select>
                  </Field>
                  <Field label="Tahun terakreditasi" hint="mis: 2023">
                    <Input type="number" min="1990" max="2100" placeholder="mis: 2023" value={editingSchool.accreditation_year ?? ""} onChange={(e) => setEditingSchool({ ...editingSchool, accreditation_year: e.target.value ? Number(e.target.value) : null })} data-testid="edit-accreditation-year" />
                  </Field>
                  <Field label="Rasio guru : siswa" hint="Otomatis dari jumlah">
                    <Input placeholder="mis: 1:15" value={editingSchool.teacher_to_student_ratio || ""} onChange={(e) => setEditingSchool({ ...editingSchool, teacher_to_student_ratio: e.target.value })} data-testid="edit-ratio" />
                  </Field>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <Field label="Jumlah siswa" hint="Total keseluruhan">
                    <Input type="number" min="0" placeholder="mis: 850" value={editingSchool.students_count ?? ""} onChange={(e) => setEditingSchool({ ...editingSchool, students_count: e.target.value ? Number(e.target.value) : null })} data-testid="edit-students-count" />
                  </Field>
                  <Field label="Jumlah guru" hint="Total tenaga pengajar">
                    <Input type="number" min="0" placeholder="mis: 55" value={editingSchool.teachers_count ?? ""} onChange={(e) => setEditingSchool({ ...editingSchool, teachers_count: e.target.value ? Number(e.target.value) : null })} data-testid="edit-teachers-count" />
                  </Field>
                </div>
                <Field label="Prestasi alumni" hint="Pisahkan dengan koma atau baris baru">
                  <Textarea rows={3} placeholder="mis: 3 alumni diterima di ITB tahun 2024, juara olimpiade sains…" value={editingSchool.alumni_achievements || ""} onChange={(e) => setEditingSchool({ ...editingSchool, alumni_achievements: e.target.value })} data-testid="edit-alumni" />
                </Field>
              </div>

              {/* NPSN — verifikasi Kemdikbud */}
              <NpsnVerifier
                value={editingSchool.npsn}
                onChange={(v) => setEditingSchool({ ...editingSchool, npsn: v })}
                schoolId={editingSchool.school_id}
                verified={!!editingSchool.government_verified}
                onLookup={(payload) => {
                  const k = payload.kemdikbud || {};
                  setEditingSchool((prev) => ({
                    ...prev,
                    government_verified: true,
                    address_street: prev.address_street || k.alamat_jalan || "",
                    kecamatan: prev.kecamatan || (k.kecamatan || "").replace(/^Kec\.\s*/, ""),
                    latitude: prev.latitude || k.lintang || null,
                    longitude: prev.longitude || k.bujur || null,
                  }));
                }}
                testidPrefix="edit-npsn"
              />

              <SchoolPhotosManager schoolId={editingSchool.school_id} />
              <ReviewPhotosPicker
                schoolId={editingSchool.school_id}
                currentImageUrl={editingSchool.image_url || ""}
                onThumbnailSet={(url) => setEditingSchool({ ...editingSchool, image_url: url })}
              />
              <AlumniOutcomes schoolId={editingSchool.school_id} />
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setEditingSchool(null)} className="rounded-full">Batal</Button>
                <Button onClick={saveSchoolEdits} data-testid="edit-save" className="rounded-full bg-[#1E3A8A] hover:bg-[#1E40AF] text-white">Simpan</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {previewImg && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-6" onClick={() => setPreviewImg(null)}>
          <img src={previewImg} alt="bukti" className="max-w-full max-h-full rounded-xl shadow-lg" />
        </div>
      )}

      {/* Static page editor dialog */}
      <Dialog open={editingPage2 !== null} onOpenChange={(v) => { if (!v) { setEditingPage2(null); setPageForm2({ ...EMPTY_PAGE_FORM }); } }}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editingPage2 === "new" ? "Halaman baru" : "Edit halaman"}</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-2" data-testid="page-editor">
            <Field label="Judul halaman" required>
              <Input type="text" autoComplete="off" autoFocus placeholder="mis: Tentang Kami" value={pageForm2.title}
                onChange={(e) => { const v = e.target.value; setPageForm2((p) => ({ ...p, title: v })); }}
                data-testid="page-title-input" />
            </Field>
            <Field label="URL slug" required hint={editingPage2 !== "new" ? "Slug tidak bisa diubah setelah halaman dibuat." : "Huruf kecil, tanpa spasi. Contoh: tentang-kami"}>
              <Input type="text" autoComplete="off" placeholder="mis: tentang-kami" value={pageForm2.slug}
                onChange={(e) => { const v = e.target.value; setPageForm2((p) => ({ ...p, slug: v })); }}
                data-testid="page-slug-input" disabled={editingPage2 !== "new"} />
            </Field>
            <Field label="Isi halaman" hint="Gunakan editor untuk memformat teks. Beralih ke mode HTML dari toolbar untuk mengedit kode langsung.">
              <BlogEditor
                value={pageForm2.body}
                onChange={(html) => setPageForm2((p) => ({ ...p, body: html }))}
                placeholder="Tulis isi halaman di sini…"
              />
            </Field>
            <label className="inline-flex items-center gap-2 text-sm text-[#1F2937] cursor-pointer">
              <input type="checkbox" checked={pageForm2.published}
                onChange={(e) => { const v = e.target.checked; setPageForm2((p) => ({ ...p, published: v })); }}
                data-testid="page-published-checkbox" className="accent-[#166534]" />
              Publikasikan sekarang (tampilkan di footer)
            </label>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => { setEditingPage2(null); setPageForm2({ ...EMPTY_PAGE_FORM }); }} className="rounded-full">Batal</Button>
              <Button onClick={savePage} data-testid="page-save-btn" className="rounded-full bg-[#1E3A8A] hover:bg-[#1E40AF] text-white">
                {editingPage2 === "new" ? "Buat halaman" : "Simpan perubahan"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Ban dialog with reason + duration */}
      <Dialog open={!!banDialogUser} onOpenChange={(v) => { if (!v) { setBanDialogUser(null); setBanForm({ reason: "", duration_days: 0 }); } }}>
        <DialogContent>
          <DialogHeader><DialogTitle>Nonaktifkan akun</DialogTitle></DialogHeader>
          {banDialogUser && (
            <div className="space-y-3 mt-2">
              <div className="bg-[#FEF2F2] border border-[#FCA5A5] rounded-xl p-3 text-sm">
                <div className="text-[#1F2937] font-medium">{banDialogUser.name}</div>
                <div className="text-[#B91C1C] text-xs">{banDialogUser.email}</div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-widest text-[#4B5563] mb-1">Durasi</div>
                <Select value={String(banForm.duration_days)} onValueChange={(v) => setBanForm({ ...banForm, duration_days: Number(v) })}>
                  <SelectTrigger data-testid="ban-duration"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="7">7 hari</SelectItem>
                    <SelectItem value="30">30 hari</SelectItem>
                    <SelectItem value="90">90 hari</SelectItem>
                    <SelectItem value="0">Permanen</SelectItem>
                  </SelectContent>
                </Select>
                <div className="text-[10px] text-[#4B5563] mt-1">Ban sementara akan otomatis dilepas setelah durasi berakhir.</div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-widest text-[#4B5563] mb-1">Alasan (akan ditampilkan ke pengguna & dicatat di log)</div>
                <Textarea rows={3} placeholder="mis: Spam, ujaran kebencian, mempromosikan sekolah tanpa izin…" value={banForm.reason} onChange={(e) => setBanForm({ ...banForm, reason: e.target.value })} data-testid="ban-reason" />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => { setBanDialogUser(null); setBanForm({ reason: "", duration_days: 0 }); }} className="rounded-full">Batal</Button>
                <Button onClick={submitBan} data-testid="ban-submit" className="rounded-full bg-[#B91C1C] hover:bg-[#991B1B] text-white">
                  <Ban size={14} className="mr-1" /> Nonaktifkan
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Review moderator application dialog */}
      <Dialog open={!!reviewingApp} onOpenChange={(v) => { if (!v) { setReviewingApp(null); setReviewNote(""); } }}>
        <DialogContent className="max-w-md" data-testid="mod-app-review-dialog">
          <DialogHeader>
            <DialogTitle>{reviewingApp?.__action === "approve" ? "Setujui aplikasi" : "Tolak aplikasi"}</DialogTitle>
          </DialogHeader>
          {reviewingApp && (
            <div className="space-y-3 mt-2">
              <div className="rounded-xl bg-[#F3F1ED] p-3 text-sm text-[#1F2937]">
                <div className="font-semibold">{reviewingApp.name}</div>
                <div className="text-xs text-[#4B5563]">{reviewingApp.email}</div>
              </div>
              {reviewingApp.__action === "approve" && (
                <div className="text-xs text-[#4B5563]">Bila email <strong>{reviewingApp.email}</strong> sudah terdaftar sebagai akun parent, peran akan otomatis dinaikkan menjadi <strong>moderator</strong> dan pengguna mendapat notifikasi in-app.</div>
              )}
              <div>
                <div className="text-xs uppercase tracking-widest text-[#4B5563] mb-1">Catatan (opsional)</div>
                <Textarea rows={3} value={reviewNote} onChange={(e) => setReviewNote(e.target.value)} data-testid="mod-app-review-note" maxLength={400} placeholder="Catatan yang akan dicatat di log admin…" />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => { setReviewingApp(null); setReviewNote(""); }} className="rounded-full h-10">Batal</Button>
                <Button onClick={() => reviewModApp(reviewingApp.__action)} data-testid="mod-app-review-submit"
                  className={"rounded-full h-10 text-white " + (reviewingApp.__action === "approve" ? "bg-[#1E3A8A] hover:bg-[#1E40AF]" : "bg-[#B91C1C] hover:bg-[#991B1B]")}>
                  {reviewingApp.__action === "approve" ? "Setujui" : "Tolak"}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

const actionBadgeCls = (action) => {
  if (action.startsWith("user_ban") || action.includes("delete")) return "bg-[#FEE2E2] text-[#B91C1C] border border-[#FCA5A5]";
  if (action.startsWith("user_unban") || action.includes("unpin")) return "bg-[#DCFCE7] text-[#166534] border border-[#BBF7D0]";
  if (action.includes("pin")) return "bg-[#FED7AA] text-[#9A3412] border border-[#FDBA74]";
  if (action.includes("tier")) return "bg-[#DBEAFE] text-[#1E3A8A] border border-[#93C5FD]";
  return "bg-[#EAE7E0] text-[#1F2937]";
};

const StatCard = ({ label, value, accent = "#1F2937" }) => (
  <div className="bg-white border border-[#EAE7E0] rounded-2xl p-5">
    <div className="text-xs uppercase tracking-widest text-[#4B5563] inline-flex items-center gap-2"><BarChart3 size={12} /> {label}</div>
    <div className="font-serif-display text-4xl mt-2" style={{ color: accent }}>{value}</div>
  </div>
);
